import React from "react";

import moment from 'moment';
import TextareaAutosize from 'react-textarea-autosize';

import { isEnterKey, LANGUAGE_ACCENT_MAP, secondsSinceEpoch } from "../helpers";
import { compare, PUNCTUATION_REGEX, sameText } from "../sentenceDiff";
import { buildTranscribeMask, foldAnswerText, isStrictDiacriticsSetting } from "../diacritics";
import { trackAmplitudeEvent, setAmplitudeUserProperties } from "../amplitude";

import AccentButtons from "./PlayCollection/AccentButtons";
import AddSentenceToCollectionModal from "./AddSentenceToCollectionModal";
import ClozeSentence from "./ClozeSentence";
import ClozeSentenceSearchModal from "./ClozeSentenceSearchModal";
import CollectionClozeSentenceEditorModal from "./CollectionClozeSentenceEditorModal";
import CollectionClozeSentenceExplanation from "./CollectionClozeSentenceExplanation";
import CopySentencePopover from "./CopySentencePopover";
import DiscussionModal from "./PlayCollection/DiscussionModal";
import ExplanationRequest from "./ExplanationRequest";
import FadeIn from "./FadeIn";
import FadeOut from "./FadeOut";
import FlagSprite from "./FlagSprite";
import { canUndoFlashcardGrade, flipAnswerResult, getFlashcardLevelPoints, getFlashcardSwipeCount, getLastFlashcardGrade, getLaterFlashcardGrade, removeDuplicateAfterIndex, saveFlashcardSwipeCount, SWIPES_BEFORE_HIDING_GRADE_CORNERS } from "./PlayCollection/flashcard";
import FlashcardSwipeable from "./PlayCollection/FlashcardSwipeable";
import GameSettings from "./PlayCollection/V5GameSettings";
import HelpModal from "./PlayCollection/HelpModal";
import Hint from "./PlayCollection/Hint";
import Icon from "./Icon";
import Incorrect from "./PlayCollection/Incorrect";
import IntraRoundAd from "./PlayCollection/IntraRoundAd";
import LandingSignUp from "./PlayCollection/LandingSignUp";
import LeveledUpModal from "./PlayCollection/LeveledUpModal";
import ListeningControl from "./PlayCollection/ListeningControl";
import Loading from "./Loading";
import ManageCollectionModal from "./ManageCollectionModal";
import Modal from "./Modal";
import ModalFooterCloseBtn from "./ModalFooterCloseBtn";
import ModalProPromo from "./ModalProPromo";
import MorePracticeControls from "./PlayCollection/MorePracticeControls";
import MultipleChoiceOptions from "./PlayCollection/V5MultipleChoiceOptions";
import MaxPlayedTodayModal from "./MaxPlayedTodayModal";
import V5MaxPlayedTodayModal from "./V5MaxPlayedTodayModal";
import Panel from "./Panel";
import Points from "./PlayCollection/Points";
import ReportErrorBtn from "./PlayCollection/ReportErrorBtn";
import ReportErrorModal from "./PlayCollection/ReportErrorModal";
import { ResourceLinksEditor } from "./ResourceLinks";
import RoundHistoryModal from "./PlayCollection/RoundHistoryModal";
import RoundResults from "./PlayCollection/V5RoundResults";
import SelectionPopover from "./SelectionPopover";
import SlideDown from "./SlideDown";
// import SpeechToText from "./SpeechToText";
import Tokens from "./PlayCollection/Tokens";

const DropDownMenu = ({ button, lis, right = false }) => {
  return (
    <div className="dropup">
      {button}
      <ul className={`dropdown-menu ${right && "dropdown-menu-right"}`}>
        {lis}
      </ul>
    </div>
  );
};

// Fallback for the strict diacritics copy, which the play views pass in translated into
// the user's app language (config/locales/strict_diacritics.yml).
const STRICT_DIACRITICS_COPY_EN = {
  title: "Strict diacritics",
  subtitle: "Require accents and other diacritics to be typed exactly",
  a11yLabel: "toggle strict diacritics",
  accentsOnlyHint: "Only the accents were off. You can turn off strict diacritics in the game settings."
};

const PlayCollectionModes = {
  flashcard: "flashcard",
  fullTextInput: "full_text_input",
  multipleChoice: 'multiple_choice',
  textInput: 'text_input'
};


export default class PlayCollection extends React.Component {
  // Chinese and Japanese have no inter-word spaces, so the comparison panel diffs
  // them per character. Clozemaster uses three-letter codes.
  static SPACELESS_LANGUAGE_CODES = ["cmn", "jpn"];

  // Once ever, per browser - the web analogue of mobile's persisted discovery flag.
  static STRICT_DIACRITICS_HINT_SHOWN_KEY = "strictDiacriticsHintShown";

  constructor(props) {
    super(props);

    this.state = Object.assign(this.getInitialGameState(), {
      audioLoopCount: 0,
      gameSettings: props.gameSettings || {},
      hasRunTutorial: props.hasRunTutorial || !!window.localStorage.getItem("hasRunPlayTutorial"),
      intraRoundAd: props.intraRoundAd,
      isAudioLooping: false,
      isRunningTutorial: false,
      translationVisible: true,
      onboarding: props.onboarding,
      explainTooltipDismissed: false
    });
  }

  componentDidMount() {
    this.setupHotkeys();
    this.initTts();
    this.initSpeaking();

    $('body')
      .tooltip({ selector: '[data-toggle="tooltip"]' })
      .on('click', (e) => {
        if(!!this.state.selectionPopoverId && $(".selection-popover").is(":visible") && !$(e.target).parents('.selection-popover, .sentence').length) {
          this.setState({
            selectionPopoverId: null,
            selectedSentenceClozeStr: null,
            selectedSentenceText: null
          });
        }
      })
      .on('mouseup', (e) => this.handleSelectionOnMouseUp(e));

    $('footer.footer a').on('click', function(e) {
      e.preventDefault();
      if(confirm('Are you sure you want to leave this page?')) {
        window.location = e.currentTarget.href;
      }
    });

    if(this.shouldShowIntraRoundAd()) {
      this.setState({
        intraRoundAdVisible: true,
        loading: false
      });
    }
    else {
      this.incrementRoundsPlayedCount();
      this.loadCollectionClozeSentences();
    }

    setAmplitudeUserProperties({
      experiment_explain_emphasis: this.props.explainEmphasisVariant || null
    });
    trackAmplitudeEvent("web_app_started_playing", this.amplitudeRoundProperties());

    if(window.localStorage.getItem("nextReviewControlsVisible") === "true") {
      this.setState({ nextReviewControlsVisible: true });
    }
  }

  componentWillUnmount() {
    if(this.state.speakingRecordingUrl) {
      URL.revokeObjectURL(this.state.speakingRecordingUrl);
    }
    if(this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
    }
    if(this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if(this.speakingAutoStopNoticeTimeout) {
      clearTimeout(this.speakingAutoStopNoticeTimeout);
    }
  }

  async startLocalRecording() {
    if(!window.MediaRecorder) {
      return;
    }

    if(!this.mediaStream) {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch(e) {
        return;
      }
    }

    if(this.mediaRecorder && this.mediaRecorder.state === "recording") {
      return;
    }

    this.recordedChunks = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.mediaStream);
      this.mediaRecorder.ondataavailable = (event) => {
        if(event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      this.mediaRecorder.onstop = () => {
        if(this.state.speakingRecordingUrl) {
          URL.revokeObjectURL(this.state.speakingRecordingUrl);
        }
        const blob = new Blob(this.recordedChunks, { type: this.mediaRecorder.mimeType });
        const speakingRecordingUrl = URL.createObjectURL(blob);
        this.setState({ speakingRecordingUrl });
      };
      this.mediaRecorder.start();
    } catch(e) {
      // swallow
    }
  }

  stopLocalRecording() {
    if(this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if(prevState.gameSettings && prevState.gameSettings.imageBackground === "on" && this.state.gameSettings.imageBackground !== "on") {
      $("#body > .content").css({ background: "" });
    }
    if(this.state.gameSettings.imageBackground === "on" &&
      (prevState.gameSettings.imageBackground !== "on" ||
        prevState.collectionClozeSentences !== this.state.collectionClozeSentences ||
        prevState.currentSentenceIndex !== this.state.currentSentenceIndex)) {

      const currentSentence = this.getCurrentSentence();
      if(currentSentence && currentSentence.imageUrl) {
        $("#body > .content").css({ background: `url(${currentSentence.imageUrl})` });
      }
      else {
        $("#body > .content").css({ background: "" });
      }
      const { collectionClozeSentences, currentSentenceIndex } = this.state;
      const nextSentence = collectionClozeSentences[currentSentenceIndex + 1];
      // this is to try to prevent flash of unstyled content with next image
      if(nextSentence && nextSentence.imageUrl) {
        $("head").append(`<link rel="preload" href="${nextSentence.imageUrl}" as="image" />`);
        $("body").append(`<style>body::after { content: url(${nextSentence.imageUrl}); display: none; }</style>`);
      }
    }

    if(this.state.nextReviewControlsVisible !== prevState.nextReviewControlsVisible) {
      if(this.state.nextReviewControlsVisible) {
        window.localStorage.setItem("nextReviewControlsVisible", "true");
      }
      else {
        window.localStorage.removeItem("nextReviewControlsVisible");
      }
    }

    if(!this.isRoundComplete(prevState) && this.isRoundComplete()) {
      this.onRoundComplete();
    }
  }

  initTts() {
    // run check to load voices
    this.isSystemTtsAvailable();
  }

  initSpeaking() {
    if(!this.isPlayingSpeaking()) {
      return false;
    }

    if(window['webkitSpeechRecognition']) {
      this.recognition = new webkitSpeechRecognition();
    }
    else if(window['SpeechRecongition']) {
      this.recognition = new SpeechRecongition();
    }
    else {
      return alert('Unable to play speaking on this browser! Sorry about that. Please try the latest Google Chrome.');
    }

    this.recognition.continuous = true;
    this.recognition.lang = this.props.targetLanguageIso.toLowerCase() + '-' + this.props.targetLanguageIso.toUpperCase();
    this.recognition.interimResults = true;
    
    this.recognition.onresult = (event) => {
      const lastResult = event.results[event.results.length - 1];
      const transcript = lastResult[0].transcript;
      this.setState({ speakingTranscript: transcript });
      if(this.state.speakingAnswerCheckMode === "live") {
        const matchingAnswer = this.findSpokenAnswer(transcript);
        if(!this.state.answered && matchingAnswer) {
          this.stopRecordingAndResetRecordBtn();
          if(this.isPlayingTextInput()) {
            this.setState({ textInputValue: transcript });
          }
          this.submitAnswer(matchingAnswer);
          return;
        }
      }
      if(lastResult.isFinal) {
        this.stopRecordingAndResetRecordBtn();
        if(this.state.speakingAnswerCheckMode !== "live") {
          const matchingAnswer = this.findSpokenAnswer(transcript);
          if(!this.state.answered && matchingAnswer) {
            if(this.isPlayingTextInput()) {
              this.setState({ textInputValue: transcript });
            }
            this.submitAnswer(matchingAnswer);
          }
        }
      }
    };

    this.recognition.onerror = (e) => this.onSpeechRecognitionError(e);
    this.recognition.onend = () => {
      if(this.state.recording) {
        this.stopRecordingAndResetRecordBtn({ autoStopped: true });
      }
    };
  }

  // Folded like typed answers, which in speaking means never strict about diacritics.
  findSpokenAnswer(transcript) {
    const fold = this.foldForComparison();
    return this.getCurrentSentenceAnswers().find((a) => fold(transcript).match(fold(a)));
  }

  onSpeechRecognitionError(e) {
    alert("Oh no! There was an error: " + e.error + " " + e.message + " - sorry about that. Speaking is an experimental feature. You may need to refresh the page or update your browser permissions. Please let us know if the issue persists. Speaking works best on the latest version of Chrome on desktop.");
    this.stopRecordingAndResetRecordBtn();
  }

  setupAudio() {
    if(this.hasSetupAudio) {
      return true;
    }

    this.correctAudio.volume = 0.25;
    this.incorrectAudio.volume = 0.25;

    this.hasSetupAudio = true;
  }

  getInitialGameState() {
    return {
      answered: false,
      collectionClozeSentences: [],
      correct: null,
      correctlyAnsweredSentenceCount: 0,
      currentSentenceIndex: 0,
      discussionModalVisible: false,
      elapsedTime: 0,
      endingInfiniteRound: false,
      flashcardFlyOut: null,
      flashcardGradeHistory: [],
      flashcardLaterGrades: [],
      flashcardRevealed: false,
      flashcardReturnFrom: null,
      flashcardUndoneGrade: null,
      hasShownTokens: false,
      intraRoundAdNextRoundable: false,
      intraRoundAdVisible: false,
      listeningControlPlayComplete: false,
      loading: true,
      multipleChoice: false, // used while playing text input
      numCorrect: 0,
      numIncorrect: 0,
      numMastered: 0,
      numNew: 0,
      numReview: 0,
      onboardingNextOptionsVisible: false,
      playedNew: {},
      playedReview: {},
      points: null,
      previousAnswer: null,
      // Redo round: a client-side replay of the round just finished. Nothing is sent to
      // /answer and nothing is rescheduled - see startRedoRound. redoCount is how many
      // replays in a row, for analytics; nextRound() clears both.
      redoCount: 0,
      redoMode: false,
      // Round-scoped, so deliberately not reset by next(). These are the Tier 0
      // indicators for Translate: num_gave_up / num_retried is the read on whether
      // exact-match grading is fair, and num_submits gives attempts-to-correct.
      roundNumGaveUp: 0,
      roundNumRetried: 0,
      roundNumSubmits: 0,
      reportErrorModalVisible: false,
      roundHistoryModalVisible: false,
      score: 0,
      sentenceEditorModalVisible: false,
      speakingTranscript: '',
      speakingRecordingSeconds: 0,
      speakingAutoStopNoticeVisible: false,
      speakingRecordingUrl: null,
      speakingAnswerCheckMode: window.localStorage.getItem("speakingAnswerCheckMode") || "final",
      startedTextInput: false,
      submitCount: 0,
      submittedInput: null,
      teachMeUsed: false,
      textInputChangedAfterAnswering: false,
      textInputValue: '',
      tokensVisible: false,
      usedHint: false
    };
  }

  setupHotkeys() {
    $(window).on('keydown', (e) => {
      // this.setState({ shiftKeyDown: e.shiftKey });

      if($('.modal, .selection-popover, .selection-slideout').is(':visible')) {
        return true;
      }

      // teaching collection name input
      if(this.isRoundComplete() && $('input').is(':focus')) {
        return true;
      }

      const { answered, gameSettings, multipleChoice, usedHint } = this.state;
      const isAnswerShown = this.isAnswerShown();

      if(this.isPlayingFlashcard() && !this.isRoundComplete() && this.onFlashcardKeyDown(e)) {
        return false;
      }

      ////////////////////////////////////////
      // enter
      if(isEnterKey(e)) {
        if(e.ctrlKey) {
          // fallback hotkey option for speaking
          if(this.isPlayingSpeaking()) {
            e.preventDefault();
            this.startRecording();
          }
        }
        else if(this.isRoundComplete()) {
          this.roundResultsPrimaryAction();
        }
        else if(answered) {
          e.preventDefault();
          this.nextWithDefaultReview();
        }
        else if((!this.isPlayingListening() || this.state.listeningControlPlayComplete) && this.sentenceTextInitiallyHiddenApplies()) {
          this.showSentenceText();
          this.justShowedSentenceTextViaEnter = true;
          return false;
        }
        else if(this.isPlayingTextInput() || this.isPlayingFullTextInput()) {
          if(!this.state.textInputValue && !this.state.startedTextInput) {
            return false;
          }
          else if(!this.state.textInputValue && gameSettings.enterSubmitsEmpty === 'off') {
            return false;
          }
          // Enter submits, it does not type. Harmless to omit for text input, where the
          // element is an <input> and Enter inserts nothing, but full text input uses a
          // textarea, so without this the keystroke both submits and leaves a stray
          // newline behind in the answer being retried.
          e.preventDefault();
          this.submitAnswer(this.state.textInputValue);
        }
      }

      if(gameSettings.hotkeys !== 'on') {
        return true;
      }

      ////////////////////////////////////////
      // space
      if(e.which === 32) {
        if(this.isRoundComplete()) {
          e.preventDefault();
          this.roundResultsPrimaryAction();
        }
        else if(e.ctrlKey) {
          e.preventDefault();
          if(e.shiftKey) {
            return this.playSentenceAudio({ speed: 0.65 });
          }
          return this.loopOrPlaySentenceAudio();
        }
        else if(e.altKey && this.isPlayingSpeaking()) {
          e.preventDefault();
          this.startRecording();
        }
        else if(answered && !this.isPlayingTextInput() && !this.isPlayingFullTextInput()) {
          e.preventDefault();
          this.nextWithDefaultReview();
        }
        else if((!this.isPlayingListening() || this.state.listeningControlPlayComplete) && this.sentenceTextInitiallyHiddenApplies()) {
          e.preventDefault();
          this.showSentenceText();
          this.justShowedSentenceTextViaEnter = true;
        }
      }

      ////////////////////////////////////////
      // right arrow
      if(e.which === 39) {
        if(this.isRoundComplete()) {
          e.preventDefault();
          this.roundResultsPrimaryAction();
        }
        else if(answered && !$(this.textInput).is(':focus')) {
          e.preventDefault();
          this.nextWithDefaultReview();
        }
      }

      ////////////////////////////////////////
      // 1-4 number keys
      if(this.multipleChoiceOptions && !answered && [49, 50, 51, 52].indexOf(e.which) >= 0) {
        e.preventDefault();
        this.multipleChoiceOptions.clickOptionAtIndex(e.which - 49);
      }
      // numpad
      if(this.multipleChoiceOptions && !answered && [97, 98, 99, 100].indexOf(e.which) >= 0) {
        e.preventDefault();
        this.multipleChoiceOptions.clickOptionAtIndex(e.which - 97);
      }
      if(this.nextReviewControlsEnabled() && this.progressControlsEnabled()) {
        // 1-3 keys
        if(isAnswerShown && this.state.nextReviewControlsVisible && [49, 50, 51].indexOf(e.which) >= 0) {
          e.preventDefault();
          this.onNextReviewControlHotkeyPress(e.which - 49);
        }
        // numpad for srs
        if(isAnswerShown && this.state.nextReviewControlsVisible && [97, 98, 99].indexOf(e.which) >= 0) {
          e.preventDefault();
          this.onNextReviewControlHotkeyPress(e.which - 97);
        }
      }
      else {
        // 1-3 keys for srs
        if(answered && this.answerQualityOptionsVisible() && [49, 50, 51].indexOf(e.which) >= 0) {
          e.preventDefault();
          this.handleAnswerQualityResponse(e.which - 49);
        }
        // numpad for srs
        if(answered && this.answerQualityOptionsVisible() && [97, 98, 99].indexOf(e.which) >= 0) {
          e.preventDefault();
          this.handleAnswerQualityResponse(e.which - 97);
        }
      }

      ////////////////////////////////////////
      // a
      if(e.which === 65 && e.altKey && isAnswerShown) {
        e.preventDefault();
        this.addCurrentSentenceToCollection();
      }

      ////////////////////////////////////////
      // c
      if(e.which === 67 && e.altKey && isAnswerShown) {
        e.preventDefault();
        this.showManageCollectionModal();
      }

      ////////////////////////////////////////
      // d
      if(e.which === 68 && e.altKey && isAnswerShown) {
        e.preventDefault();
        this.showSentenceEditorModal();
      }

      ////////////////////////////////////////
      // e
      if(e.which === 69 && e.altKey && isAnswerShown && this.state.isSignedIn) {
        e.preventDefault();
        // Report lives in the "..." menu now; there's no .report-error button to click
        this.setState({ reportErrorModalVisible: true });
      }

      ////////////////////////////////////////
      // f
      if(e.which === 70 && e.altKey && isAnswerShown) {
        e.preventDefault();
        $('.favorite-clozeable').click();
      }

      ////////////////////////////////////////
      // v / w - more practice: Speak it / Write it
      if((e.which === 86 || e.which === 87) && e.altKey && answered && this.morePracticeControlsEnabled() && this.morePracticeControls) {
        e.preventDefault();
        if(e.which === 87) {
          this.morePracticeControls.toggleWriting();
        }
        else {
          this.morePracticeControls.showSpeaking();
        }
      }

      ////////////////////////////////////////
      // g
      if(e.which === 71 && e.altKey && isAnswerShown) {
        e.preventDefault();
        this.onGrammarBtnClick();
      }

      ////////////////////////////////////////
      // h
      if(e.which === 72 && e.altKey) {
        if(this.isPlayingTextInput() && !answered && !multipleChoice && !usedHint) {
          e.preventDefault();
          this.useTextInputHint();
        }
      }

      ////////////////////////////////////////
      // i
      if(e.which === 73 && e.altKey && isAnswerShown) {
        e.preventDefault();
        this.ignoreModal.show();
      }

      ////////////////////////////////////////
      // k
      if(e.which === 75 && e.altKey && isAnswerShown && this.progressControlsEnabled()) {
        e.preventDefault();
        if(gameSettings.manualMasterResetConfirm === 'on') {
          this.knowItModal.show();
        }
        else {
          this.onKnowItClick();
        }
      }

      ////////////////////////////////////////
      // m
      if(e.which === 77 && e.altKey && isAnswerShown && this.progressControlsEnabled()) {
        e.preventDefault();
        if(gameSettings.manualMasterResetConfirm === 'on') {
          this.masterModal.show();
        }
        else {
          this.onMasterClick();
        }
      }

      ////////////////////////////////////////
      // n
      if(e.which === 78 && e.altKey && answered) {
        e.preventDefault();
        this.setState({ nextReviewControlsVisible: !this.state.nextReviewControlsVisible });
      }

      ////////////////////////////////////////
      // p
      if(e.which === 80 && e.altKey) {
        if(this.isPlayingTextInput() && !answered && !multipleChoice) {
          e.preventDefault();
          this.setState({ multipleChoice: true });
        }
      }

      ////////////////////////////////////////
      // r - on the results screen, Redo this round
      if(e.which === 82 && e.altKey && this.isRoundComplete()) {
        e.preventDefault();
        if(this.isRedoAvailable()) {
          this.startRedoRound("shortcut");
        }
      }
      else if(e.which === 82 && e.altKey && answered && this.progressControlsEnabled()) {
        e.preventDefault();
        if(gameSettings.manualMasterResetConfirm === 'on') {
          this.resetModal.show();
        }
        else {
          this.onResetClick();
        }
      }

      ////////////////////////////////////////
      // s
      if(e.which === 83 && e.altKey) {
        e.preventDefault();
        this.showSentenceSearchModal();
      }

      ////////////////////////////////////////
      // t
      if(e.which === 84 && e.altKey) { 
        e.preventDefault();
        this.toggleTranslation();
      }

      ////////////////////////////////////////
      // u
      if(e.which === 85 && e.altKey && isAnswerShown) { 
        e.preventDefault();
        this.setState({ discussionModalVisible: !this.state.discussionModalVisible });
      }

      ////////////////////////////////////////
      // x
      if(e.which === 88 && e.altKey) { 
        const currentSentence = this.getCurrentSentence();
        if(e.shiftKey) {
          if(answered && this.state.showExplanations && (currentSentence.explanation || currentSentence.explanationJobUrl)) {
            this.setState({ explanationModalVisible: true });
          }
        }
        else {
          if(currentSentence && currentSentence.imageUrl) {
            e.preventDefault();
            if(this.state.currentSentenceImageModalVisible) {
              this.currentSentenceImageModal && this.currentSentenceImageModal.hide();
            }
            else {
              this.setState({ currentSentenceImageModalVisible: !this.state.currentSentenceImageModalVisible });
            }
          }
        }
      }

      ////////////////////////////////////////
      // y
      if(e.which === 89 && e.altKey) { 
        e.preventDefault();
        this.setState({ roundHistoryModalVisible: !this.state.roundHistoryModalVisible });
      }
    });

    $(window).on('keyup', (e) => {
      // this.setState({ shiftKeyDown: e.shiftKeyDown });

      // esc
      if(e.which === 27 && this.state.leveledUpModalVisible) {
        // extra safeguard to remove leveledUpModal
        // reports of black background getting stuck
        // modal shown after answer response returns, may create race condition somewhere
        this.setState({ leveledUpModalVisible: false });
      }
    });
  }

  // full_text_input with audio as the prompt is Transcribe; with the translation as the
  // prompt it is Translate. Same mode, told apart by skill wherever they diverge.
  isPlayingTranslate() {
    return this.isPlayingFullTextInput() && !this.isPlayingListening();
  }

  isPlayingFullTextInput() {
    return this.props.mode === PlayCollectionModes.fullTextInput && !this.isPlayingSpeaking();
  }

  isPlayingMultipleChoice() {
    return this.props.mode === PlayCollectionModes.multipleChoice;
  }

  isPlayingFlashcard() {
    return this.props.mode === PlayCollectionModes.flashcard;
  }

  shortcutsMode() {
    return this.isPlayingSpeaking() ? "speaking" : this.props.mode;
  }

  isAnswerShown() {
    return this.state.answered || this.state.flashcardRevealed;
  }

  isPlayingTextInput() {
    return this.props.mode === PlayCollectionModes.textInput && !this.isPlayingSpeaking();
  }

  isPlayingListening() {
    return this.props.skill === 'listening';
  }

  isPlayingSpeaking() {
    return this.props.skill === 'speaking';
  }

  shouldShowIntraRoundAd() {
    if(!this.state.intraRoundAd || this.props.isTeachingCollection) {
      return false;
    }
    const roundsPlayedCount = this.getRoundsPlayedCount();
    return !!roundsPlayedCount && roundsPlayedCount % 4 === 0;
  }

  getRoundsPlayedCount() {
    return parseInt(window.localStorage.getItem("roundsPlayedCount")) || 0;
  }

  incrementRoundsPlayedCount() {
    const roundsPlayedCount = this.getRoundsPlayedCount();
    window.localStorage.setItem("roundsPlayedCount", "" + (roundsPlayedCount + 1));
  }

  resetRoundsPlayedCount() {
    window.localStorage.removeItem("roundsPlayedCount");
  }

  // Do whatever the primary button on the round results bottom bar does, so
  // the keyboard shortcuts match what's on screen -- when the current
  // collection has no reviews left that's "Review Next Collection!", not
  // another round of the collection we just finished.
  roundResultsPrimaryAction() {
    if(this.roundResults) {
      // Returns false when there's no primary button to press, e.g. nothing
      // left to review -- do nothing rather than start an empty round.
      return this.roundResults.triggerPrimaryAction();
    }

    this.nextRound();
    return true;
  }

  nextRound() {
    const { intraRoundAdNextRoundable, intraRoundAdVisible } = this.state;

    if(this.shouldShowIntraRoundAd() && !intraRoundAdVisible) {
      return this.setState({ intraRoundAdVisible: true });
    }

    if(intraRoundAdVisible && !intraRoundAdNextRoundable) {
      return false;
    }

    // Keep Playing from a replay's results. With redo_round_completed and a second
    // redo_round_started, this says whether a replay led back into real play.
    if(this.isRedoRound()) {
      trackAmplitudeEvent("web_app_redo_round_continued", Object.assign(
        this.amplitudeRoundProperties(),
        { redo_count: this.state.redoCount }
      ));
    }

    this.incrementRoundsPlayedCount();
    this.setState(this.getInitialGameState(), () => this.loadCollectionClozeSentences());
  }

  isRedoRound() {
    return !!this.state.redoMode;
  }

  // Known / 100% / 0% / Hard / Easy / Normal all reschedule the sentence, which is
  // exactly what a redo promises not to do, so they're hidden and inert while
  // replaying. Favorite, edit, ignore, report etc. stay - they're deliberate edits to
  // the sentence rather than grading, and they're on the results screen anyway.
  // Progress controls save through the API, so they need an account.
  progressControlsEnabled() {
    return this.props.isSignedIn && !this.isRedoRound();
  }

  // Known sets the next review to 2100 ("no more reviews").
  isKnownSentence(sentence) {
    return !!(sentence.nextReview || "").match(/2100/);
  }

  // The sentences of the round just finished, once each and in the order played
  // (incorrect answers are re-queued during a round, so there are duplicates), minus
  // anything ignored or marked Known along the way - Known means the learner is done
  // with it, so there's nothing to redo. Round-local flags are dropped so the replay
  // starts clean; ttsRequest is a Promise that wouldn't survive the clone anyway.
  getRedoSentences() {
    const seen = {};
    return this.state.collectionClozeSentences
      .filter((ccs) => {
        if(seen[ccs.id] || ccs.ignored || this.isKnownSentence(ccs)) {
          return false;
        }
        seen[ccs.id] = true;
        return true;
      })
      .map((ccs) => {
        const sentence = JSON.parse(JSON.stringify(ccs));
        delete sentence.wasIncorrect;
        delete sentence.ttsRequest;
        return sentence;
      });
  }

  // Offered even once a free account's daily cap is reached: a redo replays sentences
  // already played today and saves nothing, so it doesn't count against the cap.
  isRedoAvailable() {
    if(this.state.onboarding) {
      return false;
    }

    return this.getRedoSentences().length > 0;
  }

  // Replays the round just finished from the sentences already in memory - no request
  // to playUrl, no answers sent, no points, no level or next review changes. Everything
  // loadCollectionClozeSentences put in state (urls, settings, nextReviewByLevel) is
  // left alone since getInitialGameState doesn't touch it.
  startRedoRound(source) {
    const sentences = this.getRedoSentences();

    if(!sentences.length) {
      return false;
    }

    const redoCount = this.state.redoCount + 1;

    // previous_num_* are the round being replayed (the original, or the last replay
    // for "Redo again"), so it's visible whether it's misses that send people back.
    trackAmplitudeEvent("web_app_redo_round_started", Object.assign(
      this.amplitudeRoundProperties(),
      {
        num_sentences: sentences.length,
        previous_num_correct: this.state.numCorrect,
        previous_num_incorrect: this.state.numIncorrect,
        redo_count: redoCount,
        source
      }
    ));

    this.stopSentenceAudio();
    this.setState(Object.assign(this.getInitialGameState(), {
      collectionClozeSentences: sentences,
      currentSentenceTimerStart: secondsSinceEpoch(),
      loading: false,
      redoCount,
      redoMode: true,
      translationVisible: this.state.gameSettings.translations === 'visible'
    }), () => $("html, body").scrollTop(0));

    return true;
  }

  // Once per round, as the results screen comes up.
  onRoundComplete() {
    if(this.isRedoRound()) {
      this.onRedoRoundComplete();
    }

    if(this.isRedoAvailable()) {
      trackAmplitudeEvent("web_app_redo_round_offered", Object.assign(
        this.amplitudeRoundProperties(),
        {
          num_correct: this.state.numCorrect,
          num_incorrect: this.state.numIncorrect,
          num_sentences: this.getRedoSentences().length,
          redo_count: this.state.redoCount
        }
      ));
    }
  }

  // Each sentence of the finished round once, correct only if it was never missed -
  // a miss is re-queued, so a sentence can appear more than once in the list.
  getRoundSentenceResults() {
    const results = {};
    const order = [];
    this.state.collectionClozeSentences.forEach((ccs) => {
      if(ccs.ignored) {
        return;
      }
      if(!results[ccs.id]) {
        results[ccs.id] = { id: ccs.id, correct: true };
        order.push(ccs.id);
      }
      if(ccs.wasIncorrect) {
        results[ccs.id].correct = false;
      }
    });
    return order.map((id) => results[id]);
  }

  onRedoRoundComplete() {
    const { elapsedTime, numCorrect, numIncorrect, redoCount } = this.state;
    const sentences = this.getRoundSentenceResults();

    trackAmplitudeEvent("web_app_redo_round_completed", Object.assign(
      this.amplitudeRoundProperties(),
      {
        elapsed_time: elapsedTime,
        num_correct: numCorrect,
        num_incorrect: numIncorrect,
        num_missed_sentences: sentences.filter((s) => !s.correct).length,
        num_sentences: sentences.length,
        redo_count: redoCount
      }
    ));
  }

  loadMoreCollectionClozeSentences() {
    const { playUrl, mode, skill, scope, count } = this.props;

    if(this.isRoundComplete() || this.state.endingInfiniteRound) {
      return false;
    }

    $.ajax({
      url: playUrl,
      data: {
        collection_unit_id: this.roundCollectionUnitId(),
        count,
        explanations: true,
        mode,
        scope,
        skill
      }
    })
      .done((data) => {
        console.log(data);

        const {
          collectionClozeSentences
        } = this.state;

        const collectionClozeSentencesHash = collectionClozeSentences.reduce((h, ccs) => { h[ccs.id] = true; return h }, {});
        const newCollectionClozeSentences = data.collectionClozeSentences.filter((ccs) => !collectionClozeSentencesHash[ccs.id]);
        
        this.setState({
          loading: false,
          collectionClozeSentences: collectionClozeSentences.concat(newCollectionClozeSentences),
        });
      })
      .fail(() => {
        alert('Oh no! There was an error loading more sentences. Sorry about that. Please refresh the page to try again and let us know if you see the message again.');
      });
  }

  loadCollectionClozeSentences() {
    this.setState({ loading: true });

    const { count, mode, playUrl, skill, scope } = this.props;
    const { onboarding } = this.state;

    $.ajax({
      url: playUrl,
      data: {
        collection_unit_id: this.roundCollectionUnitId(),
        count,
        explanations: true,
        mode,
        // The landing round is the short onboarding round, without the rest of onboarding.
        onboarding: onboarding || !!this.props.landing,
        scope,
        skill
      }
    })
      .done((data) => {
        console.log(data);
        this.setState({
          loading: false,
          alreadyMasteredNextReview: data.languagePairing.alreadyMasteredNextReview,
          clozeSentencesUrl: data.languagePairing.clozeSentencesUrl,
          collectionClozeSentences: data.collectionClozeSentences,
          collectionClozeSentencesUrl: data.collection.collectionClozeSentencesUrl,
          collectionClozeSentencesUpsertUrl: data.collection.collectionClozeSentencesUpsertUrl,
          collectionClozeSentencesIgnoreAllUrl: data.collection.collectionClozeSentencesIgnoreAllUrl,
          collectionClozeSentencesAnswerUrl: data.collection.collectionClozeSentencesAnswerUrl,
          collectionClozeSentencesSrsResponseUrl: data.collection.collectionClozeSentencesSrsResponseUrl,
          collectionName: data.collection.name,
          collectionTtsAvailable: data.collection.ttsAvailable,
          collectionsUrl: data.collectionsUrl,
          currentSentenceTimerStart: secondsSinceEpoch(),
          dailyReminderEmail: data.languagePairing.dailyReminderEmail,
          dailyReminderEmailUrl: data.languagePairing.dailyReminderEmailUrl,
          gameSettingsUrl: data.languagePairing.gameSettingsUrl,
          // No ads in a round that's selling the app.
          intraRoundAd: this.props.landing ? null : data.intraRoundAd,
          isCollectionEditable: data.collection.isEditable,
          isPro: data.user.isPro,
          isSignedIn: data.user.isSignedIn,
          languagePairingId: data.languagePairing.id,
          languagePairingLevel: data.languagePairing.level,
          languagePairingUrl: data.languagePairing.url,
          maxPlayableToday: data.languagePairing.maxPlayableToday,
          nextReviewByLevel: data.languagePairing.nextReviewByLevel,
          nextReviewFuzzEnabled: data.languagePairing.nextReviewFuzzEnabled,
          numMastered: 0,
          numNew: 0,
          numPlayedToday: data.languagePairing.numPlayedToday,
          numPlaying: 0,
          numReview: 0,
          playedNew: {},
          playedReview: {},
          reportErrorUrl: data.collection.collectionClozeSentencesErrorUrl,
          resourceLinksUrl: data.languagePairing.resourceLinksUrl,
          searchTranslationEntriesUrl: data.languagePairing.searchTranslationEntriesUrl,
          showExplanations: data.showExplanations,
          startingNumMastered: data.collection.numMastered,
          startingNumPlaying: data.collection.numPlaying,
          startingNumReadyForReview: data.collection.numReadyForReview,
          tokensEnabled: data.collection.tokensEnabled,
          tokensUrl: data.collection.tokensUrl,
          userSelectionTranslationsUrl: data.languagePairing.userSelectionTranslationsUrl,
          wordBank: data.wordBank,
        });
      })
      .fail(() => {
        alert('Oh no! There was an error loading sentences. Sorry about that. Please refresh the page to try again and let us know if you see the message again.');
      });
  }

  isReviewScope() {
    return this.props.scope === 'ready_for_review';
  }

  // Review counts are collection-wide, so reviews ignore a unit in the URL.
  roundCollectionUnitId() {
    return this.isReviewScope() ? null : this.props.collectionUnitId;
  }

  isFavoritedScope() {
    return this.props.scope === 'favorited';
  }

  renderPlayingName() {
    let action = 'Playing';

    if(this.isReviewScope()) {
      action = 'Reviewing';
    }
    else if(this.isFavoritedScope()) {
      action = 'Favorites';
    }

    return (
      <div className="playing-name">
        {action}: <strong>{this.props.collectionName}</strong>
      </div>
    );
  }

  renderRoundStats() {
    const { answered, currentSentenceIndex, collectionClozeSentences, correctlyAnsweredSentenceCount, numCorrect, numIncorrect } = this.state;
    const total = collectionClozeSentences.length;
    const remaining = this.isInfiniteRound() && !this.state.endingInfiniteRound ? null : (total - currentSentenceIndex - (answered ? 1 : 0));
    const pctCorrect = total ? (numCorrect / total) * 100 : 0;
    const pctIncorrect = total ? (numIncorrect / total) * 100 : 0;

    return (
      <div className="container-fluid">
        <div ref={(el) => this.statusBar = el} style={{ padding: "8px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
            {/* Not on a landing page, where a signed-out visitor would be sent to log in. */}
            {!this.props.landing && <a
              href={this.props.languagePairingWebUrl || "/dashboard"}
              // onClick={(e) => { if(!confirm("Exit round?")) e.preventDefault(); }}
              style={{ fontSize: 50, lineHeight: 1, color: "#999", marginTop: -10, marginRight: 20, textDecoration: "none" }}
              title="Exit"
            >&times;</a>}
            <div style={{ display: "flex", gap: 10, whiteSpace: "nowrap" }}>
              <span><Icon name="ok" style={{ color: "#5cb85c" }} /> <strong className="joystix">{numCorrect}</strong></span>
              <span><Icon name="remove" style={{ color: "#d9534f" }} /> <strong className="joystix">{numIncorrect}</strong></span>
            </div>
            {(!this.isInfiniteRound() || this.state.endingInfiniteRound) ? (
              <div className="progress" style={{ flex: 1, height: 8, marginBottom: 0, borderRadius: 4 }}>
                <div className="progress-bar progress-bar-success" style={{ width: (pctCorrect + pctIncorrect) + "%", borderRadius: 4 }}></div>
              </div>
            ) : <div style={{ flex: 1 }} />}
            <span style={{ color: "#999", whiteSpace: "nowrap" }}>{remaining !== null ? <><span className="joystix">{remaining}</span> to go</> : "∞"}</span>
          </div>
        </div>
        {this.isRedoRound() && (
          <div className="v5-redo-indicator">
            <Icon name="repeat" /> Practice replay &mdash; progress not saved
          </div>
        )}
        {/*this.renderPlayingNameBanner()*/}
        {this.renderSignUpInPromo()}
        {this.renderRoundCompleteBanner()}
      </div>
    );
  }

  renderPlayingNameBanner() {
    if(this.props.isSignedIn || this.props.isTeachingCollection) {
      return (
        <div className="row bg-success text-center lead" style={{ padding: '4px 0',  marginBottom: 0 }}>
          {this.renderPlayingName()}
        </div>
      );
    }
  }

  renderSignUpInPromo() {
    // A CEFR landing page has its own sign up prompts.
    if(this.props.isSignedIn || this.props.isTeachingCollection || this.props.landing) {
      return null;
    }

    return (
      <div className="row bg-success text-center lead" style={{ padding: '4px 0', marginBottom: 0 }}>
        <a className="btn btn-sm btn-success joystix" href="/sign-up">Sign up</a>
        <span style={{ margin: '0 8px' }}>or</span>
        <a className="btn btn-sm btn-success joystix" href="/login" style={{ marginRight: 8 }}>Login</a>
        to save your progress!
      </div>
    );
  }

  renderRoundCompleteBanner() {
    if(!this.isRoundComplete()) {
      return null;
    }

    if(this.isNoSentences()) {
      return null;
    }

    return (
      <div className="row">
        <SlideDown>
          <div className={'round-complete-banner notification notice' + (this.isReviewScope() ? ' review' : '')}>
            <h1>Round Complete!</h1>
          </div>
        </SlideDown>
      </div>
    );
  }

  renderPoints() {
    if(this.state.answered && !this.state.correct && !this.state.teachMeUsed) {
      return (
        <Incorrect />
      );
    }

    return (
      <Points
        points={this.state.points}
      />
    );
  }

  renderHeader() {
    const { baseLanguageFlagIso, targetLanguageFlagIso } = this.props;
    const { answered, collectionClozeSentences, correct, currentSentenceIndex, lastPlayedDateBeforeAnswering, points, score } = this.state;
    const currentSentence = collectionClozeSentences[currentSentenceIndex];
    const pctMastered = Math.round(currentSentence.level / 4 * 100);

    return (
      <div className="stats" style={{ display: "flex", flexDirection: "row" }}>
        <div className="joystix text-left">
          <div className="content-wrapper">
            <div className="languages">
              <FlagSprite flagIso={targetLanguageFlagIso} size={24} /> / <FlagSprite flagIso={baseLanguageFlagIso} size={24} />
            </div>
            {!this.isRedoRound() && (
              <div className="score total">
                <span className="text">Score:</span> <span className="value">{score}</span>
              </div>
            )}
          </div>
        </div>
        <div style={{ flex: 1, position: "relative" }}>
          {this.renderPoints()}
        </div>
        <div className="text-right">
          <div className="content-wrapper">
            <div className="level" style={{ position: "relative" }}>
              {/*
              <div className="joystix percent-mastered">
                <span className="value">{pctMastered}%</span> Mastered
              </div>
              <div className="progress">
                <div aria-valuemax="100" aria-valuemin="0" className="progress-bar progress-bar-success" role="progressbar" style={{ width: pctMastered + '%' }}></div>
              </div>
              */}
              {this.isPlayingFlashcard() && !this.isSentenceTextHidden() && (
                <div className="flashcard-header-controls">
                  <div style={{ visibility: this.state.flashcardRevealed ? "hidden" : "visible" }}>{this.renderLevelChecks()}</div>
                  {this.renderFlashcardBackButton()}
                </div>
              )}
              {!this.isPlayingFlashcard() && (
                <>
                  {this.renderLevelChecks()}
                  <div className="joystix percent-mastered" style={{ fontSize: "0.9em" }} data-placement="bottom" data-title="Get a sentence correct 4x in a row to get it to 100% Mastered" data-toggle="tooltip">
                    <span className="value">{pctMastered}%</span> Mastered <Icon name="question-sign" />
                  </div>
                </>
              )}
              {answered && !!currentSentence.nextReview && !this.isPlayingFlashcard() && (
                <div className="next-review" data-placement="bottom" data-html="true" data-title={`<span style="display: block; text-align: left"><span style="white-space: nowrap">Num played: ${currentSentence.numPlayed}</span><br /><span style="white-space: nowrap">Num correct: ${currentSentence.numPlayed - currentSentence.numIncorrect}</span><br />${lastPlayedDateBeforeAnswering ? `<span style="white-space: pre">Last seen: ${moment().diff(lastPlayedDateBeforeAnswering, "days")} days ago</span>` : ""}</span>`} data-toggle="tooltip" style={{ marginTop: 3 }}>
                  <button className="btn btn-xs btn-default"><Icon name="stats" style={{ marginLeft: 5 }} /> Stats</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderLevelChecks() {
    const { level } = this.getCurrentSentence();

    return (
      <div style={{ color: "#aaa", fontSize: "1.5em" }}>
        <Icon name="ok-circle" style={{ color: level >= 1 ? "#5cb85c" : null, margin: "0 5px" }} />
        <Icon name="ok-circle" style={{ color: level >= 2 ? "#5cb85c" : null, margin: "0 5px" }} />
        <Icon name="ok-circle" style={{ color: level >= 3 ? "#5cb85c" : null, margin: "0 5px" }} />
        <Icon name="ok-circle" style={{ color: level >= 4 ? "#5cb85c" : null, margin: "0 5px" }} />
      </div>
    );
  }

  getCurrentSentence() {
    const { collectionClozeSentences, currentSentenceIndex } = this.state;
    return collectionClozeSentences[currentSentenceIndex];
  }

  getCurrentSentenceCloze() {
    return this.getCurrentSentence().text.split('{{')[1].split('}}')[0];
  }

  ignoreAll(callback) {
    this.setState({ updating: true });
    const currentSentence = this.getCurrentSentence();
    $.ajax({
      url: this.state.collectionClozeSentencesIgnoreAllUrl,
      method: 'put',
      data: {
        collection_cloze_sentence_id: currentSentence.id
      }
    })
      .done((data) => {
        // TODO! doesn't update other sentences that might have the same cloze
        const newCollectionClozeSentences = this.getUpdatedCollectionClozeSentences(currentSentence.id, { ignored: true });
        this.setState({
          collectionClozeSentences: newCollectionClozeSentences,
          updating: false
        }, callback);
      })
      .fail(() => {
        this.setState({ updating: false }, callback);
        alert('Oh no! There was an error updating the sentences. Sorry about that. Please try again and let us know if you see this message again.');
      });
  }

  updateCurrentSentence(attr, value, callback) {
    const currentSentence = this.getCurrentSentence();
    this.updateSentence({ sentence: currentSentence, attr, value, callback });
  }

  // expects attr string and value, or updates object
  updateSentence({ attr = null, callback, sentence, updates = null, value = null }) {
    this.setState({ updating: true });
    let update = { id: sentence.id };

    if(updates) {
      update = Object.assign(updates, update);
    }
    else {
      update[attr] = value;
    }

    $.ajax({
      url: sentence.collectionClozeSentencesUpsertUrl || this.state.collectionClozeSentencesUpsertUrl,
      method: 'post',
      dataType: 'json',
      contentType: 'application/json',
      data: JSON.stringify({ 
        updates: [update]
      })
    })
      .then((data) => {
        return $.ajax({
          data: { ids: data.ids },
          method: 'post',
          url: data.collectionClozeSentencesBatchUrl
        });
      })
      .done((data) => {
        const updated = data.collectionClozeSentences[0];
        this.setState({
          collectionClozeSentences: this.state.collectionClozeSentences.map((ccs) => updated.id === ccs.id ? Object.assign(ccs, updated) : ccs),
          updating: false
        }, callback);
      })
      .fail(() => {
        this.setState({ updating: false }, callback);
        alert('Oh no! There was an error updating. Sorry about that. Please try again and let us know if you see this message again.');
      });
  }

  renderFavoriteToggle() {
    const { isSignedIn } = this.props;

    if(!isSignedIn) {
      return null;
    }

    if(!this.state.isPro) {
      return (
        <button className="btn btn-default favorite-clozeable control" disabled={!isSignedIn} title="Favorite sentence (alt+f)" data-toggle="modal" data-target="#favorite-pro-promo-modal">
          <span className={'glyphicon glyphicon-star-empty not-favorited'}></span> Favorite
        </button>
      );
    }

    const currentSentence = this.getCurrentSentence();

    return (
      <button className={'btn btn-default favorite-clozeable control' + (currentSentence.favorited ? ' favorited active' : '')} title={`${currentSentence.favorited ? "Unf" : "F"}avorite sentence (alt+f)`} onClick={() => this.updateCurrentSentence('favorited', !currentSentence.favorited)}>
        <span className={'glyphicon glyphicon-star' + (currentSentence.favorited ? ' favorited' : '-empty not-favorited')}></span> Favorite
      </button>
    );
  }

  toggleTranslation() {
    this.setState({ translationVisible: !this.state.translationVisible });
  }

  renderTranslationsToggle() {
    const { gameSettings } = this.state;

    if(gameSettings.translations === 'visible' || (gameSettings.translations === 'show-after' && this.isAnswerShown())) {
      return null;
    }

    const button = (
      <button
        className="btn btn-default toggle-translation control"
        onClick={() => this.toggleTranslation()}
        title="Toggle translation (alt+t)"
      >
        <span className="glyphicon glyphicon-transfer"></span> Translation
      </button>
    );

    return button;
  }

  renderTransliterationToggle() {
    const { gameSettings } = this.state;

    if(gameSettings.transliteration === 'on') {
      return null;
    }

    if(!this.getCurrentSentence().transliteration) {
      return null;
    }

    return (
      <button className="btn btn-default toggle-transliteration control" onClick={() => this.setState({ transliterationVisible: !this.state.transliterationVisible })} title="Toggle transliteration">
        Transliteration
      </button>
    );
  }

  renderPronunciationToggle() {
    const { gameSettings } = this.state;

    if(gameSettings.pronunciation === 'on') {
      return null;
    }

    if(!this.getCurrentSentence().pronunciation && !this.getCurrentSentence().transliteration) {
      return null;
    }

    return (
      <button className="btn btn-default toggle-pronunciation control" onClick={() => this.setState({ pronunciationVisible: !this.state.pronunciationVisible })} title="Toggle pronunciation">
        P
      </button>
    );
  }

  // provides next letter
  useTextInputHint() {
    const textInputValue = this.state.textInputValue;
    const cloze = this.getCurrentSentenceCloze();
    let index = 0;

    while(textInputValue[index] && cloze[index] && textInputValue[index].toLowerCase() === cloze[index].toLowerCase()) {
      index = index + 1;
    }

    this.setState({
      textInputValue: cloze.substring(0, index + 1),
      usedHint: true
    }, () => this.textInput.focus());
  }

  onHintBtnClick(e) {
    $(e.currentTarget).tooltip('destroy');
    this.useTextInputHint();
  }

  renderTextInputHintBtn() {
    if(this.isPlayingSpeaking() || this.isPlayingMultipleChoice() || this.isPlayingFlashcard() || this.state.multipleChoice || this.state.usedHint) {
      return null;
    }

    return (
      <button
        className="btn btn-default text-input-hint"
        onClick={(e) => this.onHintBtnClick(e)}
        title="Single letter hint - you only get 1 per sentence and score half the points! alt+h"
      >
        <Icon name="question-sign" /> Hint
      </button>
    );
  }

  renderTextInputToMultipleChoiceBtn() {
    if(this.isPlayingSpeaking() || this.isPlayingMultipleChoice() || this.isPlayingFlashcard() || this.state.multipleChoice) {
      return null;
    }

    return (
      <button
        className="btn btn-default switch-to-multiple-choice"
        onClick={(e) => this.onSwitchToMultipleChoiceBtnClick(e)}
        title="Switch sentence to multiple choice alt+p"
      >
        <Icon name="th-large" /> Multiple choice
      </button>
    );
  }

  onSwitchToMultipleChoiceBtnClick(e) {
    $(e.currentTarget).tooltip('destroy');
    this.setState({ multipleChoice: true }, () => this.textInput.focus());
  }

  playSentenceRecording({ onEnded = null, onError = null, onPlay = null, sentence = this.getCurrentSentence(), speed = null } = {}) {
    if(this.sentenceAudio) {
      this.sentenceAudio.pause();
    }

    const { gameSettings } = this.state;

    this.sentenceAudio = new Audio(sentence.audioRecordingUrl);
    this.sentenceAudio.playbackRate = speed || parseFloat(gameSettings.textToSpeechSpeed) || 1;
    if(onPlay) {
      this.sentenceAudio.addEventListener('play', onPlay);
    }
    if(onEnded) {
      this.sentenceAudio.addEventListener('ended', onEnded);
    }
    if(onError) {
      this.sentenceAudio.addEventListener('error', onError);
    }
    this.sentenceAudio.play().catch((e) => onError && onError(e));
    return this.sentenceAudio;
  }

  playSentenceTts({ onEnded = null, onError = null, onPlay = null, sentence = this.getCurrentSentence(), speed = null } = {}) {
    if(this.sentenceAudio) {
      this.sentenceAudio.pause();
    }

    const { gameSettings } = this.state;

    this.sentenceAudio = new Audio(sentence.ttsAudioUrl);
    this.sentenceAudio.playbackRate = speed || parseFloat(gameSettings.textToSpeechSpeed) || 1;
    if(onPlay) {
      this.sentenceAudio.addEventListener('play', onPlay);
    }
    if(onEnded) {
      this.sentenceAudio.addEventListener('ended', onEnded);
    }
    if(onError) {
      this.sentenceAudio.addEventListener('error', onError);
    }
    this.sentenceAudio.play().catch(() => onError && onError());
    return this.sentenceAudio;
  }

  getSystemTtsVoices() {
    const { targetLanguageIso, targetLanguageCode } = this.props;
    return window.clozemaster.getSystemTtsVoices(targetLanguageIso, targetLanguageCode);
  }

  playSystemTts({ onEnded = null, onError = null, onPlay = null, sentence = this.getCurrentSentence(), speed = null } = {}) {
    const { targetLanguageIso, targetLanguageCode } = this.props;
    const { gameSettings } = this.state;
    const voices = this.getSystemTtsVoices();
    let voice = voices.filter((v) => v.name === gameSettings.textToSpeechVoice)[0];
    // fallback to random voice
    if(!voice) {
      voice = voices[Math.floor(Math.random()*voices.length)];
    }
    // no Web Speech API / no voices (eg Meta Quest & Wolvic VR browsers) - bail instead of throwing on voice.lang
    if(!window.speechSynthesis || !voice) {
      onError && onError();
      return null;
    }
    const rate = speed || parseFloat(gameSettings.textToSpeechSpeed) || 1;
    const u = new SpeechSynthesisUtterance(sentence.text.replace(/\{\{|\}\}/g, ''));
    u.voice = voice;
    u.lang = voice.lang;
    u.rate = window.clozemaster.isIOS8() ? (rate * 0.4) : rate;
    u.onstart = onPlay;
    u.onend = onEnded;
    u.onerror = onError;
    window.speechSynthesis.cancel();
    // timeout - seems like cancel then immediately play causes
    // onerror to be called due to cancel and then no onstart call
    // the timeout seems to allow onstart to be called as expected
    setTimeout(() => window.speechSynthesis.speak(u), 50);
    return u;
  }

  shouldLoopTextToSpeech() {
    const { gameSettings } = this.state;
    return this.isAnswerShown() && gameSettings.textToSpeechLooping === "on";
  }

  loopOrPlaySentenceAudio() {
    this.shouldLoopTextToSpeech() ? this.loopSentenceAudio() : this.playSentenceAudio();
  }

  stopSentenceAudio() {
    this.sentenceAudio && this.sentenceAudio.pause();
    window.speechSynthesis && window.speechSynthesis.cancel();
  }

  loopSentenceAudio({ looping = false } = {}) {
    const { audioLoopCount, isAudioLooping } = this.state;

    // called again to stop
    if(isAudioLooping && !looping) {
      this.stopSentenceAudio();
      this.setState({ isAudioLooping: false });
      return false;
    }
    // isAudioLooping has been set to false somewhere
    // ie when "next" is clicked, aborting
    else if(!isAudioLooping && looping) {
      return false;
    }

    this.playSentenceAudio({
      onPlay: () => {
        this.setState({
          isAudioLooping: true
        });
      },
      onEnded: () => {
        this.setState({
          audioLoopCount: audioLoopCount + 1
        });
        if(this.state.isAudioLooping) {
          this.loopSentenceAudio({ looping: true });
        }
      },
      onError: () => {
        this.setState({
          isAudioLooping: false
        });
      }
    });
  }

  playSentenceAudio({ loop = false, onEnded = null, onError = null, onPlay = null, sentence = this.getCurrentSentence(), speed = null } = {}) {
    const { gameSettings } = this.state;

    const args = {
      onEnded,
      onError,
      onPlay,
      sentence,
      speed
    };

    if(sentence.audioRecordingUrl && this.state.gameSettings.audioRecordings === "on") {
      return this.playSentenceRecording(args);
    }

    if((sentence.ttsAudioUrl || sentence.ttsUrl) && (this.state.gameSettings.clozemasterTextToSpeech === 'on' || !this.isSystemTtsAvailable())) {
      if(sentence.ttsAudioUrl) {
        return this.playSentenceTts(args);
      }

      // custom collection sentence with no audio yet - generate it now
      this.ensureTtsAudioUrl(sentence).then((ttsAudioUrl) => {
        // the user may have moved on while the audio was being generated
        if(sentence !== this.getCurrentSentence()) {
          return null;
        }

        return ttsAudioUrl ? this.playSentenceTts(args) : this.playSystemTts(args);
      });

      return null;
    }

    return this.playSystemTts(args);
  }

  // Custom collection sentences have no audio until someone asks to hear one.
  // Resolves to the url, or null if we couldn't generate it - in which case
  // the caller falls back to device tts. One attempt per sentence per session.
  ensureTtsAudioUrl(sentence) {
    if(sentence.ttsAudioUrl) {
      return Promise.resolve(sentence.ttsAudioUrl);
    }

    if(!sentence.ttsUrl) {
      return Promise.resolve(null);
    }

    // caching the request itself rather than a "tried" flag - a second play
    // while synthesis is still in flight waits on the same response instead of
    // dropping to device tts. Still one request per sentence per session,
    // since the resolved promise sticks around.
    if(!sentence.ttsRequest) {
      sentence.ttsRequest = new Promise((resolve) => {
        $.ajax({ url: sentence.ttsUrl, method: 'get' })
          .done((data) => {
            sentence.ttsAudioUrl = data.ttsAudioUrl;
            resolve(data.ttsAudioUrl);
          })
          .fail(() => resolve(null));
      });
    }

    return sentence.ttsRequest;
  }

  isTtsAvailable(sentence = this.getCurrentSentence()) {
    return sentence.ttsAudioUrl || sentence.ttsUrl || this.isSystemTtsAvailable();
  }

  isSystemTtsAvailable() {
    const { targetLanguageIso, targetLanguageCode } = this.props;
    return window.clozemaster.systemTtsAvailable(targetLanguageIso, targetLanguageCode);
  }

  renderTtsBtn() {
    if(!this.isTtsAvailable()) {
      return null;
    }

    const { audioLoopCount, isAudioLooping } = this.state;

    return (
      <div className="btn-group" role="group" aria-label="Text-to-speech controls">
        <button className={`btn btn-default control ${isAudioLooping ? "active" : ""}`} title="Play sentence audio (ctl+space)" style={{ marginRight: 0 }} onClick={() => this.loopOrPlaySentenceAudio()}>
          <span className="glyphicon glyphicon-volume-up"></span>
          {this.shouldLoopTextToSpeech() && <span className="badge tw-ml-5">{audioLoopCount}</span>} Listen
        </button>
        <button className="btn btn-default control" title="Play sentence audio half speed" style={{ margin: '0 0 0 -1px' }} onClick={() => this.playSentenceAudio({ speed: 0.65 })}>
          &frac12;
        </button>
      </div>
    );
  }

  renderAudioHintBtn() {
    return null;
    return (
      <button className="btn btn-default" title="Audio hint - half points!">
        <span className="glyphicon glyphicon-volume-up"></span> Audio hint
      </button>
    );
  }

  renderManualResetBtn() {
    if(!this.props.isSignedIn) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();
    const { gameSettings } = this.state;
    const { manualMasterResetConfirm } = gameSettings;
    const { isSignedIn } = this.props;

    return (
      <button
        className="btn btn-default manual-reset"
        data-toggle={manualMasterResetConfirm === "on" ? "modal" : null}
        data-target={manualMasterResetConfirm === "on" ? "#reset-modal" : null}
        disabled={!isSignedIn || !currentSentence.level}
        onClick={manualMasterResetConfirm === "on" ? null : () => this.onResetClick()}
        title="Reset to 0% Mastered (alt+r)"
      >
        <span className="glyphicon glyphicon-remove"></span>
      </button>
    );
  }

  renderManualMasterBtn() {
    if(!this.props.isSignedIn) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();
    const { gameSettings } = this.state;
    const { manualMasterResetConfirm } = gameSettings;
    const { isSignedIn } = this.props;

    return (
      <button
        className="btn btn-default manual-master"
        data-toggle={manualMasterResetConfirm === "on" ? "modal" : null}
        data-target={manualMasterResetConfirm === "on" ? "#master-modal" : null}
        disabled={!isSignedIn || currentSentence.level === 4}
        onClick={manualMasterResetConfirm === "on" ? null : () => this.onMasterClick()}
        title="Set this sentence to 100% Mastered (alt+m)"
      >
        <span className="glyphicon glyphicon-ok"></span>
      </button>
    );
  }

  renderKnowItBtn() {
    if(!this.progressControlsEnabled()) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();
    const { gameSettings } = this.state;
    const { manualMasterResetConfirm } = gameSettings;
    const { isSignedIn } = this.props;

    return (
      <button
        className="btn btn-default know-it"
        disabled={!isSignedIn || this.isKnownSentence(currentSentence)}
        data-toggle={manualMasterResetConfirm === "on" ? "modal" : null}
        data-target={manualMasterResetConfirm === "on" ? "#know-it-modal" : null}
        onClick={manualMasterResetConfirm === "on" ? null : () => this.onKnowItClick()}
        title="Mark this sentence as known - 100% Mastered and no more reviews (alt+k)"
      >
        <Icon name="ok-circle" /> Known
      </button>
    );
  }

  renderImageBtn() {
    const currentSentence = this.getCurrentSentence();

    if(!currentSentence.imageUrl) {
      return null;
    }

    const { answered, gameSettings } = this.state;
    
    if(!answered && gameSettings.imageToggle !== "always") {
      return null;
    }

    return (
      <button
        className="btn btn-default toggle-image control"
        onClick={() => this.setState({ currentSentenceImageModalVisible: true })}
        title="Show image (alt+x)"
      >
        <span className="glyphicon glyphicon-picture"></span> Image
      </button>
    );
  }

  nextReviewControlsEnabled() {
    return true; //this.props.nextReviewControlsEnabled || /[?&]nextReviewControlsEnabled=true/.test(location.search);
  }

  renderNextReviewToggle() {
    const { correct, nextReviewControlsVisible } = this.state;

    if(!this.progressControlsEnabled()) {
      return null;
    }

    // if(!correct) {
    //   return null;
    // }

    const days = this.answerQualityApplies() ? this.getAnswerQualityDays(1) : this.getNextReviewDays();

    return (
      <button
        className={`btn btn-default toggle-next-review-controls ${nextReviewControlsVisible ? "active" : ""}`}
        onClick={() => this.setState({ nextReviewControlsVisible: !nextReviewControlsVisible })}
        title={this.state.gameSettings.hotkeys === "on" && !this.isPlayingFlashcard() ? "Next review (alt+n)" : undefined}
      >
        <Icon name={`chevron-${nextReviewControlsVisible ? "up" : "down"}`} /> Next review: {days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"}`}
      </button>
    );
  }

  getNextReviewDays(multiplier = 1) {
    const { nextReviewByLevel } = this.state;
    const currentSentence = this.getCurrentSentence();
    const level = this.isFlashcardPick() ? Math.min(currentSentence.level + 1, 4) : currentSentence.level;
    return Math.floor(nextReviewByLevel[level] * multiplier);
  }

  // A revealed, ungraded flashcard: picking an interval is the grade, so the card moves on without an answer.
  isFlashcardPick() {
    return this.isPlayingFlashcard() && !this.state.answered && !this.state.flashcardUndoneGrade;
  }

  nextAfterReviewPick() {
    if(this.isFlashcardPick()) {
      this.flyOutFlashcard("right");
    }
    else {
      this.next();
    }
  }

  getAnswerQualityDays(quality) {
    if(this.state.alreadyMasteredNextReview === 'srs') {
      const currentSentence = this.getCurrentSentence();
      if(currentSentence.srsNextReviewDays) {
        // same quality -> SRS response mapping as handleAnswerQualityResponse
        return [currentSentence.srsNextReviewDays.hard, currentSentence.srsNextReviewDays.normal, currentSentence.srsNextReviewDays.easy][quality];
      }
      return null;
    }
    const { nextReviewByLevel } = this.state;
    const m = (quality === 0 ? 0.5 : quality);
    return Math.ceil(nextReviewByLevel[4] * m);
  }

  getNextReviewDate(multiplier = 1) {
    return moment().add(this.getNextReviewDays(multiplier), "days").format("YYYY-MM-DD");
  }

  onNextReviewControlHardSelected() {
    if(this.answerQualityApplies()) {
      this.handleAnswerQualityResponse(0);
      return;
    }
    this.updateCurrentSentence(
      "next_review",
      this.getNextReviewDate(0.5)
    );
    this.nextAfterReviewPick();
  }

  onNextReviewControlDefaultSelected() {
    if(this.answerQualityApplies()) {
      this.handleAnswerQualityResponse(1);
      return;
    }
    this.nextAfterReviewPick();
  }

  onNextReviewControlEasySelected() {
    if(this.answerQualityApplies()) {
      this.handleAnswerQualityResponse(2);
      return;
    }
    this.updateCurrentSentence(
      "next_review",
      this.getNextReviewDate(2)
    );
    this.nextAfterReviewPick();
  }

  onNextReviewControlHotkeyPress(number) {
    if(!this.progressControlsEnabled()) {
      return false;
    }

    switch(number) {
      case 0:
        this.onNextReviewControlHardSelected();
        break;
      case 2:
        this.onNextReviewControlEasySelected();
        break;
      default:
        this.onNextReviewControlDefaultSelected();
    }
  }

  nextReviewControlsShown() {
    return this.nextReviewControlsEnabled() && this.state.nextReviewControlsVisible && this.progressControlsEnabled();
  }

  renderNextReviewControls() {
    const { correct, nextReviewByLevel } = this.state;

    if(!this.nextReviewControlsShown()) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();
    const { level } = currentSentence;

    // TODO!
    // X use tw classes for mobile sizing
    // X hook up remaining buttons
    // X hot keys
    // X remember toggle
    // X turn on just if query param present
    // TODO!
    // X use tw classes for mobile sizing
    // X hook up remaining buttons
    // X hot keys
    // X remember toggle
    // X turn on just if query param present
    const daysLabel = (days) => days === null ? "" : days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"}`;

    const hotkeysOn = this.state.gameSettings.hotkeys === "on";
    const btn = ({ days, label, mastered = null, onClick, shortcut }) => (
      <button
        className="btn btn-default"
        disabled={this.state.updating}
        onClick={onClick}
        style={{ flex: 1 }}
        title={shortcut && hotkeysOn ? `${label} (${shortcut})` : undefined}
      >
        <strong>{label}</strong>
        <br />
        <small>{daysLabel(days)}</small>
        {/*!!mastered && <span className="hidden-xs"><br /><span className="joystix" style={{ fontSize: 10 }}>{mastered}% Mastered</span></span>*/}
      </button>
    );

    const normalDays = this.getNextReviewDays();

    return (
      <div style={{ margin: "0 auto", maxWidth: 600/*, overflowY: "scroll"*/ }}>
        <div className="btn-group" role="group" aria-label="Next review controls" style={{ display: "flex", width: "100%" }}>
          {[
            { days: this.answerQualityApplies() ? this.getAnswerQualityDays(0) : this.getNextReviewDays(0.5), label: "Hard", onClick: () => this.onNextReviewControlHardSelected(), shortcut: "1" },
            { days: this.answerQualityApplies() ? this.getAnswerQualityDays(2) : this.getNextReviewDays(2), label: "Easy", onClick: () => this.onNextReviewControlEasySelected(), shortcut: "3" },
            { days: 0, label: "0% Mastered", mastered: "0", onClick: () => { this.updateCurrentSentence("level", 0); this.nextAfterReviewPick(); } },
            ...(level !== 4 ? [{ days: nextReviewByLevel[4], label: "100% Mastered", mastered: "100", onClick: () => { this.updateCurrentSentence("level", 4); this.nextAfterReviewPick(); } }] : [])
          ].map((props, i) => (
            <React.Fragment key={i}>{btn(props)}</React.Fragment>
          ))}
          <button
            className={'btn btn-default ignore' + (currentSentence.ignored ? ' active' : '')}
            disabled={!this.props.isSignedIn}
            title={`${currentSentence.ignored ? "Uni" : "I"}gnore sentence (alt+i)`}
            data-toggle="modal"
            data-target="#ignore-modal"
            style={{ color: "unset", flex: 1 }}
          >
            <strong>Ignore</strong>
            <br />
            <small>Never</small>
          </button>
        </div>
      </div>
    );
  }

  renderCurrentSentenceControls() {
    const { gameSettings, nextReviewControlsVisible } = this.state;

    if(!this.isAnswerShown()) {
      return (
        <div className="controls">
          {this.renderAudioHintBtn()}
          {this.renderTranslationsToggle()}
          {this.renderTextInputToMultipleChoiceBtn()}
          {this.renderTextInputHintBtn()}
          {this.renderImageBtn()}
        </div>
      );
    }

    const { isSignedIn } = this.props;
    const currentSentence = this.getCurrentSentence();

    const defaultControls = (
      <>
        {this.renderTtsBtn()}
        {this.renderImageBtn()}
        {this.renderTranslationsToggle()}
        {this.renderPronunciationToggle()}
        {this.renderTransliterationToggle()}
      </>
    );

    const defaultNextReviewControls = (
      <>
        {this.renderKnowItBtn()}
        {this.renderManualMasterBtn()}
        {this.renderManualResetBtn()}
      </>
    );

    let nextReviewControls = defaultNextReviewControls;
    if(this.nextReviewControlsEnabled()) {
      nextReviewControls = this.renderNextReviewToggle();
    }

    return (
      <div>
        <div className="controls">
          {defaultControls}
          {this.renderFavoriteToggle()}
          {this.renderKnowItBtn()}
          {nextReviewControls}
          {/*
          <button className={'btn btn-default ignore control' + (currentSentence.ignored ? ' active' : '')} disabled={!isSignedIn} title={`${currentSentence.ignored ? "Uni" : "I"}gnore sentence (alt+i)`} data-toggle="modal" data-target="#ignore-modal" style={{ color: "unset" }}>
            <span className="glyphicon glyphicon-ban-circle"></span>
          </button>
          */}
          {this.renderGrammarBtn()}
          {/*Discuss <span className="badge comment-count"><span className="value">0</span></span>*/}

          
          {/*
          <span style={{ position: "relative" }}>
            <button className="btn btn-default control copy-to-collection" disabled={!isSignedIn} title="Copy to collection" onClick={() => this.setState({ copyPopoverVisible: true })}>
              <Icon name="copy" type="fa" />
            </button>
            {this.renderCopyCurrentSentenceToCollectionPopover()}
          </span>
          */}
        </div>
        {this.renderNextReviewControls()}
      </div>
    );
  }

  renderGrammarBtn() {
    const currentSentence = this.getCurrentSentence();
    const { tokensEnabled, tokensVisible } = this.state;
    const { isSignedIn } = this.props;

    if(isSignedIn && tokensEnabled && currentSentence.tokensCount) {
      return (
        <button className="btn btn-default control grammar" title="Grammar (alt+g)" onClick={this.onGrammarBtnClick.bind(this)}>
          <Icon name={tokensVisible ? "chevron-up" : "chevron-down"} />
        </button>
      );
    }
  }

  onGrammarBtnClick() {
    const { hasShownTokens, tokensVisible } = this.state;

    this.setState({ 
      hasShownTokens: hasShownTokens || tokensVisible,
      tokensVisible: !tokensVisible
    });
  }

  showSentenceEditorModal() {
    this.setState({ sentenceEditorModalVisible: true });
  }

  onTextInputBeforeInput(e) {
    const { answered, gameSettings } = this.state;

    // prevent text input change when opening modal via shortcut hotkey
    // a, c, d, e, f, i, m, r, u, v, w
    if(gameSettings.hotkeys === "on" && answered && e.altKey && [65, 67, 68, 69, 70, 73, 77, 82, 85, 86, 87].indexOf(e.which) >= 0) {
      e.preventDefault();
      return true;
    }
  }

  onTextInputKeyDown(e) {
    const { answered, gameSettings } = this.state;

    if(answered && gameSettings.keyDownClearsTextInputAfterAnswering === "on" && !this.afterAnswerKeyDownTriggered) {
      // if enter then skip
      if(e.which === 13) {
        return true;
      }
      this.afterAnswerKeyDownTriggered = true;
      this.setState({ textInputValue: '' });
    }
    this.handleAccentsOnKeyDown(e);
  }

  onTextInputKeyUp(e) {
    if(this.justShowedSentenceTextViaEnter) {
      this.justShowedSentenceTextViaEnter = false;
      return false;
    }

    if(!this.state.startedTextInput) {
      this.setState({ startedTextInput: true });
    }
    this.handleAccentsOnKeyUp(e);
  }

  onTextInputChange(e) {
    this.setState({
      textInputChangedAfterAnswering: this.state.answered,
      textInputValue: e.target.value
    });
  }

  handleAccentsOnKeyDown(e) {
    // not ideal, getting tick mark when using send_keys
    // suspect related to https://github.com/SeleniumHQ/selenium/issues/2704
    if(this.props.test && e.which === 0) {
      e.preventDefault();
      return false;
    }

    if(this.state.gameSettings.accentShortcuts !== 'on') {
      return null;
    }

    const caretPos = this.textInput.selectionStart;
    const c = String.fromCharCode(e.which);
    const accents = (LANGUAGE_ACCENT_MAP[this.props.targetLanguageIso] || {})[c.toUpperCase()];
    const accentDownCount = this.state.accentDownCount || 0;

    if(!e.altKey || !accents) {
      return null;
    }

    e.preventDefault();

    if(!accentDownCount) {
      this.accentingText = this.state.textInputValue;
    }

    let accent = accents[accentDownCount % accents.length];
    if(e.shiftKey) {
      accent = accent.toUpperCase();
    }

    const t = this.accentingText;
    this.setState({
      accentDownCount: (accentDownCount || 0) + 1,
      textInputValue: t.substring(0, caretPos) + accent + t.substring(caretPos)
    }, () => {
      this.textInput.selectionStart = caretPos;
      this.textInput.selectionEnd = caretPos + 1;
    });
  }

  handleAccentsOnKeyUp(e) {
    // not ideal, getting tick mark when using send_keys
    // suspect related to https://github.com/SeleniumHQ/selenium/issues/2704
    if(this.props.test && e.which === 0) {
      e.preventDefault();
      return false;
    }

    if(this.state.gameSettings.accentShortcuts !== 'on') {
      return null;
    }

    // 17 = ctrl key, 18 = alt key
    if(e.which === 18 && this.state.accentDownCount > 0) {
      const caretPos = this.textInput.selectionStart;
      this.setState({ accentDownCount: 0 });
      this.textInput.selectionStart = caretPos + 1;
      this.textInput.selectionEnd = caretPos + 1;
    }
  }

  getCurrentSentenceAnswers() {
    const currentSentence = this.getCurrentSentence();
    const cloze = this.getCurrentSentenceCloze();
    return [cloze].concat(currentSentence.alternativeAnswers || []);
  }

  isTextInputCorrectSoFar() {
    const fold = this.foldForComparison();
    const typed = fold(this.state.textInputValue || '');
    return !!this.getCurrentSentenceAnswers().find((a) => fold(a).indexOf(typed) === 0);
  }

  onAddToCollectionClick() {
    // show modal with custom sentence based on selection
    //
    // show modal
    this.setState({
      addSentenceToCollectionModalVisible: true,
      selectionPopoverId: null, // hide the popover
    }, () => this.clearSelection()); // clear the selection to ensure popover stays closed
    // native check button - we post this sentence for people learning from target language to confirm whether sentence is correct
  }

  onSearchCollectionClick() {
    this.showManageCollectionModal();
  }

  showManageCollectionModal() {
    this.setState({
      manageCollectionModalVisible: true,
      selectionPopoverId: null
    });
  }

  onSearchSentencesClick() {
    this.showSentenceSearchModal();
  }

  showSentenceSearchModal() {
    const { answered } = this.state;

    if(!answered) {
      return null;
    }

    this.setState({
      clozeSentenceSearchModalVisible: true,
      selectionPopoverId: null
    });
  }

  getSelectionPopoverProps() {
    const currentSentence = this.getCurrentSentence();
    return {
      baseLanguageIso: this.props.baseLanguageIso,
      baseLanguageCode: this.props.baseLanguageCode,
      gameSettings: this.state.gameSettings,
      gameSettingsUrl: this.state.gameSettingsUrl,
      isPro: this.state.isPro,
      isSearchCollectionAvailable: !!this.props.collectionId,
      onClose: () => this.onSelectionPopoverClose(),
      onAddToCollectionClick: () => this.onAddToCollectionClick(),
      onSearchCollectionClick: () => this.onSearchCollectionClick(),
      onSearchSentencesClick: () => this.onSearchSentencesClick(),
      openResourceLinksEditor: () => this.openResourceLinksEditor(),
      searchTranslationEntriesUrl: this.state.searchTranslationEntriesUrl,
      targetLanguageCode: this.props.targetLanguageCode,
      targetLanguageEnglishName: this.props.targetLanguageEnglishName,
      targetLanguageIso: this.props.targetLanguageIso,
      tokenizeableId: currentSentence.id,
      tokenizeableType: "CollectionClozeSentence",
      userSelectionTranslationsUrl: this.state.userSelectionTranslationsUrl
    };
  }

  openResourceLinksEditor() {
    this.onSelectionPopoverClose();
    this.setState({
      resourceLinksEditorVisible: true
    });
  }

  renderCloze() {
    const { mode } = this.props;
    const { answered, gameSettings, textInputValue } = this.state;
    const currentSentence = this.getCurrentSentence();
    const cloze = this.getCurrentSentenceCloze();

    if(this.isPlayingTextInput()) {
      return (
        <input
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          className={'input ' + this.getTextInputColorClassName()}
          name="text_input_value"
          onChange={(e) => this.onTextInputChange(e)}
          onKeyDown={(e) => this.onTextInputKeyDown(e)}
          onKeyUp={(e) => this.onTextInputKeyUp(e)}
          placeholder={(this.state.teachMeUsed || this.state.answered) ? cloze : undefined}
          ref={(el) => {
            this.textInput = el;

            // if(!!this.textInput && typeof InputEvent.prototype.getTargetRanges === "function") {
            //   if(this.textInputBeforeInputListener) {
            //     this.textInput.removeEventListener("beforeinput", this.textInputBeforeInputListener);
            //   }
            //   this.textInputBeforeInputListener = this.textInput.addEventListener("beforeinput", this.onTextInputBeforeInput.bind(this));
            // }
          }}
          spellCheck="false"
          style={(answered || gameSettings.fitTextInputWidth === 'on') ? { width: this.getAnswerWidth() } : { width: $(window).width() < 1024 ? 140 : 200 }}
          type="text"
          value={textInputValue}
        />
      );
    }

    if(this.isAnswerShown()) {
      return (
        <SelectionPopover
          {...this.getSelectionPopoverProps()}
          selection={cloze}
          visible={this.state.selectionPopoverId === 'cloze'}
        >
          <strong
            className="cloze"
            role="button"
            onClick={(e) => {
              this.setState({
                selectionPopoverId: this.state.selectionPopoverId === 'cloze' ? null : 'cloze',
                selectedSentenceClozeStr: currentSentence.text,
                selectedSentenceText: cloze
              });
            }}
          >
            <u>{cloze}</u>
          </strong>
        </SelectionPopover>
      );
    }

    return (
      <span>__________</span>
    );
  }

  getTextInputColorClassName() {
    const { answered, correct, gameSettings, textInputChangedAfterAnswering, textInputValue } = this.state;

    if(answered && !textInputChangedAfterAnswering) {
      return correct ? 'correct' : '';
    }

    return (answered || gameSettings.typingColorHint === 'on')
      && textInputValue 
      && (this.isTextInputCorrectSoFar() ? 'correct' : 'incorrect');
  }

  getAnswerWidth() {
    const cloze = this.getCurrentSentenceCloze();
    $('<span id="width">').text(cloze).appendTo('.stage');
    const width = $('#width').width() + 10;
    $('#width').remove();
    return width;
  }

  onWordClick(word, id, startIndex) {
    const text = this.getCurrentSentencePlainText();
    this.setState({
      selectedSentenceClozeStr: text.substring(0, startIndex) + text.substring(startIndex).replace(word, "{{" + word + "}}"),
      selectedSentenceText: word,
      selectionPopoverId: this.state.selectionPopoverId === id ? null : id
    });
  }

  renderWord(word, id, startIndex) {
    // when i click a word or select text, need to show popover
    return (
      <SelectionPopover
        {...this.getSelectionPopoverProps()}
        key={id}
        selection={word}
        visible={this.state.selectionPopoverId === id}
      >
        <span className="word" role="button" onClick={(e) => this.onWordClick(word, id, startIndex)}>{word}</span>
      </SelectionPopover>
    );
  }

  wordify({ text, idPrefix }) {
    const regex = /[^\s.,;\?!]+/g;
    let startIndex = 0;
    // using #!# as our divider
    return text
      .replace(regex, "#!#$&#!#")
      .split(/#!#/g)
      .map((x, i) => {
        const word = !!x.match(regex) ?  this.renderWord(x, (idPrefix || '') + i, startIndex) : x;
        startIndex += x.length;
        return word;
      });
  }

  getCurrentSentencePlainText() {
    return this.getSentencePlainText(this.getCurrentSentence().text);
  }

  getSentencePlainText(text) {
    return text.replace(/{{|}}/g, '');
  }

  // https://stackoverflow.com/questions/3169786/clear-text-selection-with-javascript
  clearSelection() {
    if (window.getSelection) {
      if (window.getSelection().empty) {  // Chrome
        window.getSelection().empty();
      } else if (window.getSelection().removeAllRanges) {  // Firefox
        window.getSelection().removeAllRanges();
      }
    } else if (document.selection) {  // IE?
      document.selection.empty();
    }
  }

  onSelectionPopoverClose() {
    this.clearSelection();
    this.setState({
      selectionPopoverId: null,
      selectedSentenceClozeStr: null,
      selectedSentenceText: null
    });
  }

  handleSelectionOnMouseUp(e) {
    // returning true so it doesn't interfere with ccs editor onmouseup
    // (and maybe other mouseups on react elements)

    if($(e.target).parents('.selection-popover, .selection-slideout').length) {
      return true;
    }

    if($(e.target).parents('.modal').length) {
      return true;
    }

    const selection = window.getSelection();
    let str = selection.toString();
    const { answered } = this.state;

    if(!str.length && this.textInput && $(this.textInput).is(':focus')) {
      str += this.textInput.value.substring(this.textInput.selectionStart, this.textInput.selectionEnd);
    }

    if(!str.length || !answered) {
      return true;
    }

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer.parentNode.cloneNode(false);
    container.appendChild(range.cloneContents());
    const html = container.innerHTML;
    const $selection = $('<span>' + html + '</span>');

    if($selection.find('.input').length && $selection.find('.pre, .post').length) {
      $selection.find('.input').replaceWith(this.state.textInputValue);
      str = $selection.text().replace(/\n/g, ' ').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
    }

    if(!this.getCurrentSentencePlainText().match(str)) {
      return true;
    }

    e.preventDefault();
    e.stopPropagation();

    // timeout to skip listener on body that clears selection
    setTimeout(() => {
      this.setState({
        selectionPopoverId: 'sentence',
        selectedSentenceClozeStr: this.getSelectionClozeStr(selection),
        selectedSentenceText: str
      });
    }, 50);
  }

  getSelectionClozeStr(selection) {
    let start = selection.anchorOffset;
    let end = selection.focusOffset;
    let anchorNode = selection.anchorNode;
    let focusNode = selection.focusNode;

    if(start > end) {
      start = end;
      end = selection.anchorOffset;
      anchorNode = focusNode;
      focusNode = selection.anchorNode;
    }

    const dfs = (node) => {
      if(node.nodeName === "#text") {
        if(node === anchorNode && node === focusNode) {
          return node.textContent.substring(0, start) + '{{' + node.textContent.substring(start, end) + '}}' + node.textContent.substring(end);
        }
        if(node === anchorNode) {
          return node.textContent.substring(0, start) + '{{' + node.textContent.substring(start);
        }
        if(node === focusNode) {
          return node.textContent.substring(0, end) + '}}' + node.textContent.substring(end);
        }
        return node.textContent;
      }

      if(node['name'] === "text_input_value") {
        if(node === anchorNode && node === focusNode) {
          return node.value.substring(0, start) + '{{' + node.value.substring(start, end) + '}}' + node.value.substring(end);
        }
        if(node === anchorNode) {
          return node.value.substring(0, start) + '{{' + node.value.substring(start);
        }
        if(node === focusNode) {
          return node.value.substring(0, end) + '}}' + node.value.substring(end);
        }
        return node.value;
      }

      let str = '';
      for(let i = 0, n = node.childNodes.length; i < n; i++) {
        str += dfs(node.childNodes[i]);
      }
      return str;
    };

    return dfs($('.sentence')[0]);
  }

  getCurrentSentenceParts() {
    const currentSentence = this.getCurrentSentence();
    let parts = currentSentence.text.split('{{');
    const preClozeStr = parts[0];
    parts = parts[1].split('}}');
    return { preClozeStr, cloze: parts[0], postClozeStr: parts[1] };
  }

  onTokenClick({ cloze, text }) {
    const currentSentence = this.getCurrentSentence();
    this.setState({
      selectedSentenceClozeStr: currentSentence.text.replace(cloze || text, "{{" + (cloze || text) + "}}"),
      selectedSentenceText: text,
      selectionPopoverId: "sentence"
    });
  }

  renderTokens() {
    const { hasShownTokens, isPro, tokensUrl, tokensVisible } = this.state;
    const { targetLanguageCode, targetLanguageIso } = this.props;

    if(!tokensVisible) {
      return null;
    }

    if(!isPro && hasShownTokens) {
      return (
        <SlideDown>
          <div className="alert alert-success" style={{ maxWidth: 600, margin: "10px auto 10px", padding: "20px 10px 20px" }}>
            <p style={{ fontSize: "1.5em", fontWeight: "bold", marginBottom: 10 }}>Get full access to sentence grammar with Clozemaster Pro - subscribe today and get fluent faster!</p>
            <p><a className="btn btn-success btn-lg joystix" href="/pro">Get Pro!</a></p>
          </div>
        </SlideDown>
      );
    }

    const currentSentence = this.getCurrentSentence();

    return (
      <SlideDown>
        <Tokens
          onClick={this.onTokenClick.bind(this)}
          targetLanguageCode={targetLanguageCode}
          targetLanguageIso={targetLanguageIso}
          tokenizeableId={currentSentence.id}
          tokenizeableType={"CollectionClozeSentence"}
          url={tokensUrl || currentSentence.tokensUrl}
        />
      </SlideDown>
    );
  }

  // The comparison panel: what you wrote over what was expected, each line marking only
  // the tokens the other lacks. del/ins rather than colour alone, so the strikethrough
  // and underline carry the same information for anyone who can't tell red from green.
  renderComparison() {
    const { answered, submittedInput, textInputValue } = this.state;

    if(submittedInput === null || answered || !this.isPlayingFullTextInput()) {
      return null;
    }

    const spaceless = this.spacelessLanguage();
    // Diffed against the live input, not the frozen submitted text, so marks clear as
    // the answer is corrected. The masked skeleton above the textarea already updates
    // per keystroke, so live is the idiom here - and because an exact match auto-submits
    // on change, converging to zero marks and grading is one continuous motion.
    // submittedInput stays the trigger for the panel, and the gate in submitAnswer.
    const { attempt, expected, identical } = compare(this.getCurrentSentencePlainText(), textInputValue, {
      // No trailing whitespace means the last word is still being typed, so it should
      // not be marked as an error yet.
      lastTokenInProgress: !/\s$/.test(textInputValue),
      normalizeToken: this.foldForComparison(),
      spaceless
    });

    const joiner = spaceless ? "" : " ";
    const renderLine = (tokens, Tag, className) => (
      <span style={this.props.targetLanguageRtl ? { direction: "rtl" } : null}>
        {tokens.map((t, i) => (
          <React.Fragment key={i}>
            {i > 0 ? joiner : ""}
            {t.wrong ? <Tag className={className}>{t.token}</Tag> : t.token}
          </React.Fragment>
        ))}
      </span>
    );

    return (
      <div className="answer-comparison panel panel-default" style={{ margin: "10px auto", maxWidth: 500, textAlign: "left" }}>
        <div className="panel-body" style={{ padding: "8px 12px" }}>
          <div style={{ marginBottom: 6 }}>
            <small className="text-muted" style={{ display: "block" }}>Your answer</small>
            {renderLine(attempt, "del", "text-danger")}
          </div>
          <div>
            <small className="text-muted" style={{ display: "block" }}>Expected</small>
            {renderLine(expected, "ins", "text-success")}
          </div>
          <small className="text-muted" style={{ display: "block", marginTop: 6 }}>
            {/* identical should be unreachable: compare() and sameText() share the fold,
                so anything compare() calls identical the grade already accepted. Saying
                so beats rendering two apparently equal lines with no explanation, and
                beats returning null - a submit that visibly does nothing is worse than
                either. */}
            {identical
              ? "That doesn't quite match - check the spacing and punctuation."
              : "Fix your answer above and submit again, or submit again unchanged to move on."}
          </small>
        </div>
      </div>
    );
  }

  // Enter submits; it must not also type. Prevented on the element that would receive
  // the newline rather than relying on the window-level hotkey handler, which bails out
  // early whenever a modal or selection popover happens to be open - a stray newline in
  // the answer you are being asked to fix is the wrong thing to leave riding on an
  // unrelated guard.
  //
  // Submitting stays with the window handler. React's listeners sit on the document,
  // which bubbles before window, so preventing here still lets that handler run; doing
  // both here would submit twice, and a second unchanged submit grades the answer
  // instead of opening the retry panel.
  // The other half of the accent shortcuts, and it is not optional. handleAccentsOnKeyUp
  // does two things when alt is released: it resets accentDownCount to 0, and it moves
  // the caret past the character just inserted.
  //
  // Without it, accentDownCount never returns to 0, which breaks the next accent in two
  // ways. The `if(!accentDownCount)` guard in handleAccentsOnKeyDown stops refreshing
  // this.accentingText, so the handler keeps rebuilding the field from text captured
  // before the round moved on - the previous sentence's answer reappears in the input.
  // And the inserted character stays selected, so the next keystroke replaces it.
  //
  // Both were reported from the wild by a Norwegian learner using alt+a for å/æ. Text
  // input never hit this because its <input> has always had an onKeyUp; the full-text
  // textarea had no key handlers at all until Enter needed one, and accents were wired
  // to the keydown side alone.
  onFullTextInputKeyUp(e) {
    this.handleAccentsOnKeyUp(e);
  }

  onFullTextInputKeyDown(e) {
    if(isEnterKey(e) && !e.ctrlKey) {
      e.preventDefault();
    }
    // Accent shortcuts (alt+a and friends) never worked here, because this handler did
    // not exist and handleAccentsOnKeyDown was only wired to the single-word input. The
    // accent buttons did work, which is presumably why nobody noticed.
    this.handleAccentsOnKeyDown(e);
  }

  renderCurrentSentenceFullTextInput() {
    const { baseLanguageRtl, targetLanguageRtl } = this.props;
    const currentSentence = this.getCurrentSentence();
    let { preClozeStr, cloze, postClozeStr } = this.getCurrentSentenceParts();
    const plainText = (preClozeStr + cloze + postClozeStr);
    const { answered, gameSettings, textInputValue } = this.state;
    const fold = this.foldForComparison();
    const text = buildTranscribeMask(plainText, textInputValue, { fold, punctuationRegex: PUNCTUATION_REGEX });

    return (
      <div>
        <div
          className={"sentence" + (targetLanguageRtl ? " rtl" : "")}
          style={{ fontSize: "2em" }}
        >
          {text}
        </div>
        <TextareaAutosize
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          // Green while on track, and in Translate no red. The check is a prefix match
          // against the one accepted answer, so composing a translation that words
          // anything differently turns the whole box red and leaves it red - and the
          // comparison panel now reports the actual difference on submit, which is the
          // job the live signal was doing badly. Transcribe keeps the red: there you are
          // reproducing a sentence you just heard, so diverging really is a mistake.
          className={`input ${!!textInputValue && gameSettings.typingColorHint === "on" ? (fold(plainText).indexOf(fold(textInputValue)) === 0 ? "correct" : (this.isPlayingTranslate() ? "" : "incorrect")) : ""} form-control text-center`}
          name="text_input_value"
          onChange={(e) => this.setState({ textInputValue: e.target.value }, () => {
            if(this.isCorrectAnswer(this.state.textInputValue)) {
              this.submitAnswer(this.state.textInputValue);
            }
          })}
          onKeyDown={(e) => this.onFullTextInputKeyDown(e)}
          onKeyUp={(e) => this.onFullTextInputKeyUp(e)}
          ref={(el) => this.textInput = el}
          spellCheck="false"
          style={{ fontSize: "1.5em", margin: "15px auto", maxWidth: 500, padding: 5 }}
          value={textInputValue}
        />
        {this.renderComparison()}
        {(!this.isPlayingListening() || gameSettings.translations === 'visible' || this.state.translationVisible) && (
          <div
            className={"translation " + (baseLanguageRtl ? "rtl " : "") + ("font-size-" + gameSettings.translationFontSize)}
          >
            {currentSentence.translation}
          </div>
        )}
        {!!currentSentence.pronunciation && answered && (gameSettings.pronunciation === 'on' || this.state.pronunciationVisible) && (
          <>
            <div>{currentSentence.pronunciation}</div>
          </>
        )}
        {!!currentSentence.transliteration && answered && (gameSettings.transliteration === 'on' || this.state.transliterationVisible) && (
          <>
            <div>{currentSentence.transliteration}</div>
          </>
        )}
        {this.renderAccentButtons()}
        <div style={{ marginTop: 10 }}>
          <button
            className="btn btn-default btn-lg text-input-hint"
            onClick={() => {
              const { textInputValue } = this.state;
              const text = this.getCurrentSentencePlainText();
              let i = 0;
              while(text[i] && (textInputValue[i] && textInputValue[i].toLowerCase() === text[i].toLowerCase())) {
                i = i + 1;
              }
              this.setState({ textInputValue: text.substring(0, i + 1) }, () => {
                this.textInput && this.textInput.focus();
              });
            }}
            title="Hint!"
          >
            <Icon name="question-sign" />
          </button>
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            className="btn btn-success joystix btn-lg"
            onClick={() => this.submitAnswer(textInputValue)}
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  showSentenceText() {
    this.setState({ sentenceTextVisible: true }, () => this.textInput && this.textInput.focus());
  }

  renderCurrentSentence() {
    if(this.isListening()) {
      return null;
    }

    if(!this.state.answered && this.isPlayingFullTextInput()) {
      return this.renderCurrentSentenceFullTextInput();
    }

    const { gameSettings, tokensVisible } = this.state;
    const answered = this.isAnswerShown();
    const currentSentence = this.getCurrentSentence();
    let { preClozeStr, cloze, postClozeStr } = this.getCurrentSentenceParts();
    const { baseLanguageRtl, targetLanguageRtl } = this.props;

    if(answered) {// && !targetLanguageCode.match(/^cmn|^jpn/)) {
      preClozeStr = this.wordify({ text: preClozeStr, idPrefix: 'pre' });
      postClozeStr = this.wordify({ text: postClozeStr, idPrefix: 'post' });
    }

    let sentence = (
      <SelectionPopover
        {...this.getSelectionPopoverProps()}
        selection={this.state.selectedSentenceText}
        visible={this.state.selectionPopoverId === 'sentence'}
      >
        <span className="pre">{preClozeStr}</span>{this.renderCloze()}<span className="post">{postClozeStr}</span>
      </SelectionPopover>
    );

    const isHintVisible = !answered && !!currentSentence.hint && gameSettings.hints === 'on';
    // A tooltip gets clipped by the flashcard, so like mobile the flashcard shows the hint under the translation.
    if(isHintVisible && !this.isPlayingFlashcard()) {
      sentence = (
        <Hint
          fontSize={gameSettings.hintFontSize}
          hint={currentSentence.hint}
        >
          {sentence}
        </Hint>
      );
    }

    const content = (
      <>
        <div className={'sentence' + (answered ? ' answered' : '') + (targetLanguageRtl ? ' rtl' : '')}>
          {this.isSentenceTextHidden() ? (
            <button aria-label="show sentence" className="btn btn-default btn-lg" onClick={() => this.showSentenceText()} style={{ width: 120 }}><Icon name="eye-open" style={{ fontSize: "1.5em" }} /></button>
          ) : sentence}
        </div>
        {(this.isPlayingTranslate() || gameSettings.translations === 'visible' || this.state.translationVisible) && (
          <div
            className={"translation " + (baseLanguageRtl ? "rtl " : "") + ("font-size-" + gameSettings.translationFontSize)}
          >
            {currentSentence.translation}
          </div>
        )}
        {isHintVisible && this.isPlayingFlashcard() && (
          <div className={"flashcard-hint font-size-" + gameSettings.hintFontSize}>{currentSentence.hint}</div>
        )}
        {!!currentSentence.pronunciation && answered && (gameSettings.pronunciation === 'on' || this.state.pronunciationVisible) && (
          <div
            className={"pronunciation font-size-" + gameSettings.pronunciationFontSize}
          >
            {currentSentence.pronunciation}
          </div>
        )}
        {!!currentSentence.transliteration && answered && (gameSettings.transliteration === 'on' || this.state.transliterationVisible) && (
          <div
            className={"transliteration font-size-" + gameSettings.transliterationFontSize}
          >
            {currentSentence.transliteration}
          </div>
        )}
        {answered && !!currentSentence.notes && <div className={"notes font-size-" + gameSettings.notesFontSize}><strong>Notes:</strong> {currentSentence.notes}</div>}
        {answered && tokensVisible && this.renderTokens()}
        {!this.isSentenceTextHidden() && (
          <>
            {this.renderAccentButtons()}
            {!this.isPlayingFlashcard() && this.renderCurrentSentenceControls()}
          </>
        )}
      </>
    );

    return content;
  }

  renderAccentButtons() {
    if(this.isPlayingMultipleChoice() || this.isPlayingFlashcard() || this.isPlayingSpeaking()) {
      return null;
    }

    if(this.state.answered) {
      return null;
    }

    let extras = [];
    if(this.isPlayingFullTextInput()) {
      const punctuation = this.getCurrentSentencePlainText().match(PUNCTUATION_REGEX);
      if(punctuation) {
        extras = punctuation.filter((v, i, a) => a.indexOf(v) === i);
      }
    }

    return (
      <AccentButtons
        accentMap={LANGUAGE_ACCENT_MAP[this.props.targetLanguageIso] || {}}
        extras={extras}
        onAccentBtnClick={(e, accent) => this.onAccentBtnClick(e, accent)}
      />
    );
  }

  onAccentBtnClick(e, accent) {
    if(e.shiftKey) {
      accent = accent.toUpperCase();
    }

    const t = this.state.textInputValue;
    const caretPos = this.textInput.selectionStart;

    this.setState({
      textInputValue: t.substring(0, caretPos) + accent + t.substring(caretPos)
    }, () => {
      this.textInput.focus();
      this.textInput.selectionStart = caretPos + 1;
      this.textInput.selectionEnd = caretPos + 1;
    });
  }

  showOffBy(distance) {
    const content = '<span class="off-by">Almost! Off by ' + distance + ' letter' + (distance === 1 ? '' : 's') + '.</span>';
    if($('.off-by').length) {
      $('.off-by').replaceWith(content);
    }
    else {
      $(this.textInput).popover({ content, html: true, trigger: 'manual', placement: 'top' }).popover('show');
    }
  }

  sendAnswer({ correct, timeDiff }) {
    // A redo is a replay: the answer was already recorded the first time round.
    if(this.isRedoRound()) {
      return false;
    }

    if(!this.props.isSignedIn) {
      if(this.props.landing) {
        this.sendLandingAnswer({ correct, timeDiff });
      }
      return false;
    }

    const { collectionClozeSentencesAnswerUrl, gameSettings, multipleChoice, usedHint } = this.state;
    const currentSentence = this.getCurrentSentence();
    const { mode, skill, scope } = this.props;

    // fire off answer
    $.ajax({
      contentType: "application/json",
      data: JSON.stringify({
        id: currentSentence.id,
        correct,
        // full_text_input reaches the server as itself now that the daily stats trackers
        // count it - num_transcribe / num_translate, split by skill. Points and
        // auto-master only ever branch on multiple_choice, so nothing else changes.
        mode: (this.isPlayingMultipleChoice() || this.isPlayingFlashcard() || multipleChoice) ? PlayCollectionModes.multipleChoice : mode,
        skill,
        playing_favorites: this.isFavoritedScope(),
        time: timeDiff,
        used_hint: usedHint
      }),
      method: 'put',
      url: currentSentence.collectionClozeSentencesAnswerUrl || collectionClozeSentencesAnswerUrl,
    })
      .done((data) => {
        const leveledUp = data.languagePairing.level > this.state.languagePairingLevel;
        this.setState({
          languagePairingLevel: data.languagePairing.level,
          leveledUp: this.state.leveledUp || leveledUp,
          leveledUpModalVisible: gameSettings.leveledUpNotifications === 'on' && leveledUp
        });
      })
      .fail(() => {
        alert('Oh no! There was an error saving that answer. Sorry about that. Your progress up until now has been saved. Please refresh the page and try again, and let us know if you see this message again.');
      });
  }

  // Logged out on a CEFR landing page: the server holds the answer in the session and
  // records it on the account at sign up. Losing one isn't worth interrupting the round for.
  sendLandingAnswer({ correct, timeDiff }) {
    const { landing, mode } = this.props;
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    const data = JSON.stringify({
      id: this.getCurrentSentence().id,
      correct,
      mode: (this.isPlayingMultipleChoice() || this.state.multipleChoice) ? PlayCollectionModes.multipleChoice : mode,
      time: timeDiff
    });
    const send = () => $.ajax({
      contentType: "application/json",
      data,
      headers: { "X-CSRF-Token": csrfToken ? csrfToken.getAttribute("content") : "" },
      method: "post",
      url: landing.answerUrl
    });

    // One at a time: the answers are held in the cookie session, so two requests
    // in flight would each save over the other's answer.
    this.landingAnswers = (this.landingAnswers || Promise.resolve()).then(send).catch(() => {});
  }

  // The fold shared by the grade, the comparison panel, the typing colour hint, the
  // transcribe mask and the off-by-N hint. Must stay character-wise: sameText applies
  // it to a whole string, compare() applies it per token.
  foldForComparison({ strictDiacritics = this.isStrictDiacritics() } = {}) {
    const { targetLanguageIso } = this.props;
    return (token) => foldAnswerText(token, { strictDiacritics, targetLanguageIso });
  }

  // Speaking is never strict, as on mobile where it defaults off per skill: speech
  // recognition drops diacritics often enough that strict would mark correct speech
  // wrong. Web keeps one settings blob per language pairing rather than one per skill,
  // so speaking cannot have its own saved value - and the toggle is hidden there anyway.
  isStrictDiacritics() {
    return !this.isPlayingSpeaking() && isStrictDiacriticsSetting(this.state.gameSettings);
  }

  spacelessLanguage() {
    return PlayCollection.SPACELESS_LANGUAGE_CODES.includes(this.props.targetLanguageCode);
  }

  // `strictDiacritics` is overridable so the discovery hint can ask whether an answer
  // graded wrong would have passed with the setting off.
  isCorrectAnswer(a, { strictDiacritics = this.isStrictDiacritics() } = {}) {
    const fold = this.foldForComparison({ strictDiacritics });

    if(this.isPlayingFullTextInput()) {
      // sameText rather than a hand-rolled compare so the panel can never contradict
      // the grade. It also collapses runs of whitespace and trims, which the previous
      // check did not: one trailing space or a doubled space mid-sentence used to make
      // a perfectly typed sentence impossible to complete.
      return sameText(a, this.getCurrentSentencePlainText(), {
        normalizeToken: fold
      });
    }

    if(this.isPlayingTextInput() && this.state.teachMeUsed) {
      return false;
    }

    const answers = this.getCurrentSentenceAnswers();
    const clean = fold(a.replace(/^\s+|\s+$/g, ''));
    return !!answers.filter((answer) => (clean === fold(answer))).length;
  }

  // Strict is on and the answer was wrong only because of its accents: point at the
  // setting, once ever. Without this almost nobody finds the toggle. Typed answers only -
  // multiple choice can't get accents wrong, and after Teach Me the answer was given.
  maybeShowStrictDiacriticsHint(a) {
    const { multipleChoice, teachMeUsed } = this.state;

    if(!this.isStrictDiacritics() || teachMeUsed || multipleChoice) {
      return;
    }
    if(!this.isPlayingTextInput() && !this.isPlayingFullTextInput()) {
      return;
    }
    if(!this.isCorrectAnswer(a, { strictDiacritics: false })) {
      return;
    }

    try {
      if(window.localStorage.getItem(PlayCollection.STRICT_DIACRITICS_HINT_SHOWN_KEY)) {
        return;
      }
      window.localStorage.setItem(PlayCollection.STRICT_DIACRITICS_HINT_SHOWN_KEY, "1");
    }
    catch(e) {
      // No storage, no way to keep it to once - better not to show it every time.
      return;
    }

    this.setState({ strictDiacriticsHintVisible: true });
    trackAmplitudeEvent("web_app_strict_diacritics_hint_shown", this.amplitudeRoundProperties());
  }

  renderStrictDiacriticsHint() {
    if(!this.state.strictDiacriticsHintVisible) {
      return null;
    }

    return (
      <div className="alert alert-info strict-diacritics-hint" role="status" style={{ margin: "10px auto", maxWidth: 500 }}>
        <button aria-label="Close" className="close" onClick={() => this.setState({ strictDiacriticsHintVisible: false })} type="button">
          <span aria-hidden="true">&times;</span>
        </button>
        {this.strictDiacriticsCopy().accentsOnlyHint}
      </div>
    );
  }

  strictDiacriticsCopy() {
    return Object.assign({}, STRICT_DIACRITICS_COPY_EN, this.props.strictDiacriticsCopy);
  }

  // `force` skips the retry gate below - used by the escape hatches ("No idea", and the
  // reveal button in the comparison panel), which mean "grade me now, I give up".
  submitAnswer(a, { force = false } = {}) {
    const currentSentence = this.getCurrentSentence();
    const cloze = this.getCurrentSentenceCloze();
    const { mode, skill, scope } = this.props;
    const {
      collectionClozeSentences,
      collectionClozeSentencesAnswerUrl,
      correctlyAnsweredSentenceCount,
      currentSentenceTimerStart,
      elapsedTime,
      gameSettings,
      multipleChoice,
      nextReviewByLevel,
      numCorrect,
      numIncorrect,
      numMastered,
      numNew,
      numReview,
      playedNew,
      playedReview,
      previousAnswer,
      score,
      teachMeUsed,
      translationVisible,
      textInputValue,
      usedHint,
    } = this.state;
    const redo = this.isRedoRound();
    const numPlayedToday = this.state.numPlayedToday + (currentSentence.wasIncorrect || redo ? 0 : 1);

    const correct = this.isCorrectAnswer(a);

    if(!correct) {
      this.maybeShowStrictDiacriticsHint(a);
    }

    // Retry before grading. A whole-sentence answer is long enough that one mistyped
    // letter invalidating the lot is the complaint this feature exists to answer, and
    // in a review round being marked wrong resets the sentence to 0% mastered. So a
    // non-matching submit shows the comparison panel and leaves the textarea editable;
    // nothing reaches /answer. Submitting the same text again means "I meant it" and
    // grades it - the same two-strike shape as the spelling-hint check below.
    //
    // usedHint is set here rather than tracked separately so that getting there on a
    // later attempt flows through the existing half-points rule and the used_hint field
    // in the answer payload. Correct-with-hint, not incorrect: the point is to stay
    // motivating, and the Hard button is there for anyone who feels they got off easy.
    if(this.isPlayingFullTextInput() && !force && !correct && a !== this.state.submittedInput) {
      this.setState({
        // submittedInput is null only before the panel has ever opened for this
        // sentence, which makes it the "count this sentence once" test.
        roundNumRetried: this.state.roundNumRetried + (this.state.submittedInput === null ? 1 : 0),
        roundNumSubmits: this.state.roundNumSubmits + 1,
        submitCount: this.state.submitCount + 1,
        submittedInput: a,
        usedHint: true
      }, () => this.textInput && this.textInput.focus());
      return true;
    }

    let distance = 0;
    if(this.isPlayingTextInput() && !multipleChoice && !teachMeUsed && !correct && a !== previousAnswer && gameSettings.spellingHints === 'on' && Levenshtein && (distance = Levenshtein.get(this.foldForComparison()(a), this.foldForComparison()(cloze))) <= 2) {
      this.setState({ previousAnswer: a });
      this.showOffBy(distance);
      return true;
    }
    $(this.textInput).popover('destroy');

    const updatedCollectionClozeSentences = JSON.parse(JSON.stringify(collectionClozeSentences));

    let points = 0;
    const levelWas = currentSentence.level;
    let level = currentSentence.level;
    if(correct) {
      if(this.isFavoritedScope()) {
        points = correct ? 2 : 0;
      }
      else {
        level = Math.min(currentSentence.level + 1, 4);
        points = level * ((this.isPlayingMultipleChoice() || this.isPlayingFlashcard() || multipleChoice) ? 4 : 8);
        if(usedHint) {
          points = points * 0.5;
        }
        if(currentSentence.nextReview && currentSentence.nextReview > moment().format("YYYY-MM-DD")) {
          points = points * 0.5;
        }
      }
    }
    else {
      if(!this.isFavoritedScope()) {
        level = 0;
      }
      currentSentence.wasIncorrect = true;
      updatedCollectionClozeSentences.push(currentSentence);
    }

    // Redo: still grade, and still re-queue a miss so it comes back this replay, but
    // the sentence keeps its level and next review and nothing scores.
    if(redo) {
      level = levelWas;
      points = 0;
    }

    const countsAsNew = !redo && !currentSentence.nextReview;
    const countsAsReview = !redo && !countsAsNew && !playedNew[currentSentence.id] && !playedReview[currentSentence.id] && !!currentSentence.nextReview;
    const nextReview = this.isFavoritedScope() ? currentSentence.nextReview : moment().add(nextReviewByLevel[level], 'days').format("YYYY-MM-DD");

    updatedCollectionClozeSentences.forEach((ccs) => {
      if(ccs.id === currentSentence.id && !redo) {
        ccs.level = level;
        ccs.nextReview = nextReview;
        ccs.numIncorrect += (correct ? 0 : 1);
        ccs.numPlayed += 1;
      }
    });

    // Math.max(..., 0) - negative time_played temporary bug fix
    // TODO! if time secondsSinceEpoch - currentSentenceTimerStart is < 0, send notification with debug info
    // const timeDiff = Math.min(Math.max(secondsSinceEpoch() - currentSentenceTimerStart, 0), 30); // max 30 seconds per answer
    const timeDiff = Math.max(secondsSinceEpoch() - currentSentenceTimerStart, 0);

    this.sendAnswer({ correct, timeDiff });

    $('[data-toggle="tooltip"]').tooltip('hide');

    this.afterAnswerKeyDownTriggered = false;
    this.setState({
      answered: true,
      answerSubmitted: a,
      collectionClozeSentences: updatedCollectionClozeSentences,
      correct,
      correctlyAnsweredSentenceCount: correctlyAnsweredSentenceCount + (correct ? 1 : 0),
      elapsedTime: elapsedTime + timeDiff,
      lastPlayedDateBeforeAnswering: currentSentence.lastPlayedDate,
      numCorrect: numCorrect + (correct ? 1 : 0),
      numIncorrect: numIncorrect + (correct ? 0 : 1),
      numMastered: numMastered + (!redo && currentSentence.level < 4 && level === 4 ? 1 : 0),
      numNew: numNew + (countsAsNew ? 1 : 0),
      numPlayedToday,
      numReview: numReview + (countsAsReview ? 1 : 0),
      playedNew: countsAsNew ? Object.assign({}, playedNew, { [currentSentence.id]: true }) : playedNew,
      playedReview: countsAsReview ? Object.assign({}, playedReview, { [currentSentence.id]: true }) : playedReview,
      points,
      pronunciationVisible: false,
      roundNumGaveUp: this.state.roundNumGaveUp + (this.state.submittedInput !== null && !correct ? 1 : 0),
      roundNumSubmits: this.state.roundNumSubmits + (this.isPlayingFullTextInput() ? 1 : 0),
      score: score + points,
      alreadyMastered: levelWas === level && level === 4,
      textInputValue: cloze,
      translationVisible: (gameSettings.translations === 'hidden' && translationVisible) || gameSettings.translations === 'show-after',
      transliterationVisible: false
    }, () => {
      if(!this.isPlayingFlashcard()) {
        if((!!currentSentence.audioRecordingUrl || this.isTtsAvailable()) && gameSettings.textToSpeech === 'on') {
          this.loopOrPlaySentenceAudio();
        }
        this.playAnswerSoundEffect(correct);
      }

      if(this.props.showExplainEmphasisTooltip
        && this.state.currentSentenceIndex === 0
        && (currentSentence.explanation || currentSentence.explanationJobUrl)) {
        trackAmplitudeEvent("web_app_explain_tooltip_shown", this.amplitudeRoundProperties());
      }
    });
  }

  playAnswerSoundEffect(correct) {
    if(this.state.gameSettings.soundEffects !== 'on' || this.state.teachMeUsed) {
      return false;
    }

    this.setupAudio();

    this[(correct ? 'correct' : 'incorrect') + 'Audio'].play();
  }

  endInfiniteRound() {
    const updatedCollectionClozeSentences = this.state.collectionClozeSentences.filter((s, i) => {
      return i <= this.state.currentSentenceIndex || s.wasIncorrect;
    });

    this.setState({
      collectionClozeSentences: updatedCollectionClozeSentences,
      endingInfiniteRound: true
    }, () => this.next());

    // this.setState({
    //   collectionClozeSentences: JSON.parse(JSON.stringify(this.state.collectionClozeSentences)).slice(0, this.state.currentSentenceIndex + 1),
    //   currentSentenceIndex: this.state.currentSentenceIndex + 1 // or call next
    // });
  }

  renderEndInfiniteRoundBtn() {
    if(!this.isInfiniteRound() || this.state.endingInfiniteRound) {
      return null;
    }

    return (
      <button
        className="btn btn-danger text-center end-round"
        data-title="Don't stop! Keep going!"
        data-toggle="tooltip"
        onClick={() => this.endInfiniteRound()}
        style={{ marginRight: 10 }}
      >
        <Icon name="stop" /> End Round
      </button>
    );
  }

  answerQualityApplies() {
    const currentSentence = this.getCurrentSentence();
    return this.progressControlsEnabled() &&
      !this.isFavoritedScope() &&
      !!this.state.alreadyMasteredNextReview &&
      this.state.alreadyMastered &&
      !(currentSentence.nextReview || "").match(/2100/) &&
      currentSentence.level === 4;
  }

  // replace with game setting
  // always show next review controls for 100% Mastered sentences
  answerQualityOptionsVisible() {
    return this.answerQualityApplies() && !this.nextReviewControlsEnabled();
  }

  renderNextBtn() {
    if(!this.state.answered) {
      return null;
    }

    let control = null;
    if(this.answerQualityOptionsVisible()) {
      control = (
        <span className="srs-controls">
          <button className="btn btn-lg btn-danger" onClick={() => this.handleAnswerQualityResponse(0)}>
            <small>1. </small><span className="joystix">Hard</span>
          </button>
          <button className="btn btn-lg btn-warning" style={{ marginLeft: 10, marginRight: 10 }} onClick={() => this.handleAnswerQualityResponse(1)}>
            <small>2. </small><span className="joystix">Normal</span>
          </button>
          <button className="btn btn-lg btn-success" onClick={() => this.handleAnswerQualityResponse(2)}>
            <small>3. </small><span className="joystix">Easy</span>
          </button>
        </span>
      );
    }
    else { // if(!this.nextReviewControlsEnabled() || !this.state.correct || !this.state.nextReviewControlsVisible) {
      control = (
        <button className="btn btn-lg btn-success next joystix" onClick={() => this.nextWithDefaultReview()} style={{ width: 140 }} title={this.state.gameSettings.hotkeys === "on" && !this.isPlayingTextInput() && !this.isPlayingFullTextInput() ? "Next (Enter / →)" : "Next (Enter)"}>
          Next
          <span className="glyphicon glyphicon-chevron-right"></span>
        </button>
      );
    }

    return (
      <div className="navigation" style={{ display: 'block' }}>
        {this.renderShowExplanationBtn()}
        {control}
      </div>
    );
  }

  amplitudeRoundProperties() {
    return {
      base_language_code: this.props.baseLanguageCode,
      target_language_code: this.props.targetLanguageCode,
      collection_id: this.props.collectionId,
      collection_name: this.props.collectionName,
      mode: this.props.mode,
      skill: this.props.skill,
      scope: this.props.scope,
      count: this.props.count,
      redo_mode: this.isRedoRound()
    };
  }

  shouldShowExplainEmphasisTooltip() {
    const { showExplainEmphasisTooltip } = this.props;
    const { answered, currentSentenceIndex, explainTooltipDismissed } = this.state;
    return !!showExplainEmphasisTooltip
      && currentSentenceIndex === 0
      && !!answered
      && !explainTooltipDismissed;
  }

  onExplainButtonClick() {
    const tooltipVisible = this.shouldShowExplainEmphasisTooltip();
    trackAmplitudeEvent("web_app_explain_button_clicked", Object.assign(
      this.amplitudeRoundProperties(),
      { with_emphasis_tooltip: tooltipVisible }
    ));
    this.setState({
      explanationModalVisible: true,
      explainTooltipDismissed: tooltipVisible ? true : this.state.explainTooltipDismissed
    });
  }

  onExplainTooltipDismiss() {
    trackAmplitudeEvent("web_app_explain_tooltip_dismissed", this.amplitudeRoundProperties());
    this.setState({ explainTooltipDismissed: true });
  }

  renderExplainEmphasisTooltip() {
    if(!this.shouldShowExplainEmphasisTooltip()) {
      return null;
    }

    return (
      <div
        className="popover top"
        role="tooltip"
        style={{
          display: "block",
          left: "50%",
          marginTop: -74,
          transform: "translateX(-50%)",
          width: 200
        }}
      >
        <div className="arrow" style={{ left: "50%" }}></div>
        <div className="popover-content" style={{ paddingRight: 28 }}>
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={() => this.onExplainTooltipDismiss()}
            style={{ position: "absolute", top: 4, right: 8 }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
          Tap <strong>Explain</strong> learn more about this sentence.
        </div>
      </div>
    );
  }

  renderShowExplanationBtn() {
    const { isPro, showExplanations } = this.state;
    const { isSignedIn } = this.props;

    if(!showExplanations) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();

    if(!currentSentence.explanation && !currentSentence.explanationJobUrl) {
      return null;
    }

    // // only for pro users for now
    // if(!currentSentence.explanation && !isPro) {
    //   return null;
    // }

    const emphasis = this.shouldShowExplainEmphasisTooltip();

    return (
      <div style={{ position: "relative", display: "inline-block", marginRight: 10 }}>
        {this.renderExplainEmphasisTooltip()}
        <button
          className={`btn btn-success-outline btn-lg joystix${emphasis ? " v5-explain-emphasis-pulse" : ""}`}
          onClick={() => this.onExplainButtonClick()}
          style={{ width: 140 }}
        >
          Explain
        </button>
      </div>
    );
  }

  // Moving on from a 100% Mastered review counts as Normal, whichever key or button does it.
  nextWithDefaultReview() {
    if(this.answerQualityApplies()) {
      this.handleAnswerQualityResponse(1);
    }
    else {
      this.next();
    }
  }

  handleAnswerQualityResponse(quality) {
    if(!this.progressControlsEnabled()) {
      this.next();
      return;
    }

    if(this.state.alreadyMasteredNextReview === 'srs') {
      const srsResponseQuality = [1, 4, 7];
      this.sendSrsResponse(srsResponseQuality[quality]);
    }
    else if(this.state.alreadyMasteredNextReview === 'static') {
      const { nextReviewByLevel } = this.state;
      const m = (quality === 0 ? 0.5 : quality);
      let n = Math.ceil(nextReviewByLevel[4] * m);
      // NOTE! all other fuzz applied backend
      if(n >= 8 && this.state.nextReviewFuzzEnabled) {
        const fuzz = Math.ceil(0.05 * n);
        n += (-fuzz + Math.floor(Math.random() * (fuzz * 2 + 1)));
      }
      this.updateCurrentSentence('next_review', moment().add(n, 'days').format("YYYY-MM-DD"));
    }
    else {
      alert("Something's gone terribly wrong! Sorry about that. Your answer's been saved, but not your Hard/Normal/Easy selection. Please let us know.");
    }
    this.next();
  }

  sendSrsResponse(quality) {
    const currentSentence = this.getCurrentSentence();
    this.setState({ updating: true });

    $.ajax({
      url: currentSentence.collectionClozeSentencesSrsResponseUrl || this.state.collectionClozeSentencesSrsResponseUrl,
      method: 'post',
      dataType: 'json',
      contentType: 'application/json',
      data: JSON.stringify({ 
        srs_response: {
          collection_cloze_sentence_id: currentSentence.id,
          quality
        }
      })
    })
      .done((data) => {
        this.setState({
          collectionClozeSentences: this.state.collectionClozeSentences.map((ccs) => (
            ccs.id === currentSentence.id ? data.collectionClozeSentence : ccs
          )),
          updating: false
        });
      })
      .fail(() => {
        this.setState({ updating: false });
        alert('Oh no! There was an error updating. Sorry about that. Please try again and let us know if you see this message again.');
      });
  }

  // A redo replays a fixed list, so it never loads more or offers End Round.
  isInfiniteRound() {
    return this.props.count === 'infinite' && !this.isRedoRound();
  }

  runTutorial() {
    this.setState({ isRunningTutorial: true });

    const popovers = [
      {
        selector: ".level .progress",
        opts: {
          content: "<p>Every sentence has a % Mastered.</p>",
        }
      },
      {
        selector: ".level .progress",
        opts: {
          content: "<p>Answer a sentence correctly 4 times in a row to get it to 100% Mastered.</p>",
        }
      },
      {
        selector: [".level .next-review", ".level .progress"],
        opts: {
          content: "<p>Each time a sentence is answered correctly, it's queued up to be reviewed further in the future.</p>",
        }
      },
      {
        selector: ".score.total",
        opts: {
          content: "<p>A correct answer scores points.</p>",
        }
      },
      {
        selector: ".score.total",
        opts: {
          content: "<p>A sentence with a higher % Mastered scores more points, and playing text input scores more points than multiple choice.</p>",
        }
      },
      {
        selector: ".score.total",
        opts: {
          content: "<p>An incorrect answer scores 0 points and resets the sentence to 0% Mastered.</p>",
        }
      },
      {
        selector: ".btn.game-settings",
        opts: {
          content: "<p>You can change the game settings here. Some settings are different for multiple choice vs. text input.</p>"
        }
      },
      {
        selector: [".next", ".stage .sentence"],
        opts: {
          content: "<p>That's it! 🙌 More questions? Click the Help button bottom right.</p>",
        }
      },
    ];
    let popoverIndex = 0;

    const defaultOpts = {
      animation: false,
      container: "body",
      html: true,
      placement: "bottom",
      template: `
        <div class="popover tutorial" role="tooltip">
          <div class="arrow"></div>
          <h3 class="popover-title"></h3>
          <div class="popover-content"></div>
        </div>
      `,
      trigger: "manual"
    };

    const showNextPopover = () => {
      this.clearTutorialPopovers();

      if(popoverIndex >= popovers.length) {
        this.completedTutorial();
        return false;
      }

      const { opts } = popovers[popoverIndex];
      let { selector } = popovers[popoverIndex];
      const isLastPopover = popoverIndex + 1 >= popovers.length;
      opts.content = `
        <div style="font-size: 1.25em">
          ${opts.content}
        </div>
        <div style="text-align: right">
          <span class="joystix" style="margin-right: 10px">${popoverIndex + 1}/${popovers.length}</span>
          <button class="btn btn-success joystix next">${isLastPopover ? "Done" : "Next"}</button>
          ${isLastPopover ? "" : `<button class="btn btn-default joystix skip">Skip</button>`}
        </div>
      `;

      // allows for selector to be an array with a fallback selector if the first is not present
      if(selector instanceof Array) {
        if(!$(selector[0]).length) {
          selector = selector[1];
        }
        else {
          selector = selector[0];
        }
      }

      $(selector)
        .addClass("tutorial-target")
        .popover(Object.assign({}, defaultOpts, opts))
        .popover("show");

      const $popover = $(".popover");
      const selectorTop = $(selector).offset().top;
      const popoverTop = $popover.offset().top;
      const popoverBottom = popoverTop + $popover.height();
      const screenTop = $(window).scrollTop();
      const screenHeight = $(window).height();
      const screenBottom = screenTop + screenHeight;
      let scrollTop = null;
      if(popoverBottom > screenBottom) {
        scrollTop = popoverBottom - screenHeight;
      }
      if(selectorTop < screenTop) {
        scrollTop = selectorTop - 20; // - 20 for buffer
      }
      if(scrollTop) {
        $("html, body").animate({ scrollTop });
      }
    };

    $("body").on("click.tutorial", ".popover .btn.next", () => {
      popoverIndex += 1;
      showNextPopover();
    });
    $("body").on("click.tutorial", ".popover .btn.skip", () => {
      this.completedTutorial();
    });

    showNextPopover();
  }

  clearTutorialPopovers() {
    $(".tutorial-target").popover("destroy").removeClass("tutorial-target");
  }

  completedTutorial() {
    const { isSignedIn } = this.props;
    const { hasRunTutorial } = this.state;

    this.setState({
      hasRunTutorial: true,
      isRunningTutorial: false
    }, () => this.clearTutorialPopovers());

    // if it was already true can skip update
    if(hasRunTutorial) {
      return true;
    }

    if(isSignedIn) {
      $.ajax({
        contentType: "application/json",
        data: JSON.stringify({
          user: { has_run_play_tutorial: true }
        }),
        method: "put",
        url: "/api/v1/users"
      });
    }
    else {
      window.localStorage.setItem("hasRunPlayTutorial", "true");
    }
  }

  next() {
    const { shouldRunTutorial } = this.props;
    const {
      currentSentenceIndex,
      collectionClozeSentences,
      gameSettings,
      hasRunTutorial,
      hasShownTokens,
      isRunningTutorial,
      tokensVisible
    } = this.state;

    if(this.isRoundComplete()) {
      return false;
    }

    this.stopSentenceAudio();

    if(this.isPlayingSpeaking()) {
      this.stopRecordingAndResetRecordBtn();
    }
    if(this.state.speakingRecordingUrl) {
      URL.revokeObjectURL(this.state.speakingRecordingUrl);
    }

    if(isRunningTutorial) {
      this.completedTutorial();
    }
    else if(shouldRunTutorial && !hasRunTutorial) {
      return this.runTutorial();
    }

    const laterFlashcardGrade = getLaterFlashcardGrade(this.state, currentSentenceIndex + 1);
    const keptFlashcardGrade = this.getKeptFlashcardGradeUpdates();

    this.setState(Object.assign(keptFlashcardGrade, {
      alreadyMastered: false,
      answered: false,
      audioLoopCount: 0,
      correct: null,
      points: null,
      currentSentenceIndex: currentSentenceIndex + 1,
      currentSentenceTimerStart: secondsSinceEpoch(),
      flashcardLaterGrades: laterFlashcardGrade ? this.state.flashcardLaterGrades.slice(0, -1) : this.state.flashcardLaterGrades,
      flashcardRevealed: !!laterFlashcardGrade,
      flashcardReturnFrom: null,
      flashcardUndoneGrade: laterFlashcardGrade,
      hasShownTokens: hasShownTokens || tokensVisible,
      isAudioLooping: false,
      listeningControlPlayComplete: false,
      multipleChoice: false,
      previousAnswer: null,
      sentenceTextVisible: false,
      speakingTranscript: '',
      speakingRecordingUrl: null,
      startedTextInput: false,
      strictDiacriticsHintVisible: false,
      submitCount: 0,
      submittedInput: null,
      teachMeUsed: false,
      textInputChangedAfterAnswering: false,
      textInputValue: '',
      tokensVisible: false,
      translationVisible: gameSettings.translations === 'visible' || (!!laterFlashcardGrade && gameSettings.translations === 'show-after'),
      usedHint: false
    }), () => {
      if(this.textInput) {
        this.textInput.focus();
      }
      // infinite and within N of the end, then load more
      if(this.isInfiniteRound() && this.state.currentSentenceIndex > this.state.collectionClozeSentences.length - 5) {
        this.loadMoreCollectionClozeSentences();
      }
    });
  }

  onFlashcardKeyDown(e) {
    if(e.ctrlKey || e.altKey) {
      return false;
    }
    if(e.target && e.target.closest && e.target.closest("button, a, input, select, textarea")) {
      return false;
    }

    const isSpaceOrArrow = [32, 37, 39].indexOf(e.which) >= 0;
    if(isSpaceOrArrow && this.state.gameSettings.hotkeys !== 'on') {
      return false;
    }

    if(isEnterKey(e) || e.which === 32) {
      e.preventDefault();
      if(isEnterKey(e) && this.state.flashcardRevealed) {
        this.gradeFlashcard("got_it", "key");
      }
      else if(this.isSentenceTextHidden()) {
        this.showSentenceText();
      }
      else {
        this.flipFlashcard();
      }
      return true;
    }
    if(e.which === 39 || e.which === 37) {
      e.preventDefault();
      this.gradeFlashcard(e.which === 39 ? "got_it" : "missed", "key");
      return true;
    }
    return false;
  }

  trackFlashcardEvent(eventName, properties = {}) {
    trackAmplitudeEvent(eventName, Object.assign(
      this.amplitudeRoundProperties(),
      { sentences_played: this.state.currentSentenceIndex },
      properties
    ));
  }

  revealFlashcard() {
    const { answered, flashcardRevealed, gameSettings, translationVisible } = this.state;
    if(answered || flashcardRevealed) {
      return;
    }

    this.trackFlashcardEvent("flashcard_answer_revealed");
    this.setState({
      flashcardRevealed: true,
      translationVisible: translationVisible || gameSettings.translations === 'show-after'
    }, () => {
      if((!!this.getCurrentSentence().audioRecordingUrl || this.isTtsAvailable()) && gameSettings.textToSpeech === 'on') {
        this.loopOrPlaySentenceAudio();
      }
    });
  }

  hideFlashcardAnswer() {
    const { answered, flashcardRevealed, gameSettings } = this.state;
    if(answered || !flashcardRevealed) {
      return;
    }

    this.stopSentenceAudio();
    this.trackFlashcardEvent("flashcard_answer_hidden");
    this.setState({
      flashcardRevealed: false,
      translationVisible: gameSettings.translations === 'visible'
    });
  }

  undoFlashcardGrade() {
    const { collectionClozeSentences, currentSentenceIndex, flashcardGradeHistory, flashcardLaterGrades, flashcardUndoneGrade, gameSettings, translationVisible } = this.state;
    if(!canUndoFlashcardGrade(this.state, currentSentenceIndex)) {
      return;
    }

    const flashcardLastGrade = getLastFlashcardGrade(this.state);
    const withoutRequeuedCopy = flashcardLastGrade.correct
      ? null
      : removeDuplicateAfterIndex(collectionClozeSentences, flashcardLastGrade.index, flashcardLastGrade.sentenceId);

    this.stopSentenceAudio();
    this.trackFlashcardEvent("flashcard_grade_undone", {
      result: flashcardLastGrade.correct ? "got_it" : "missed",
      sentences_played: flashcardLastGrade.index
    });
    this.setState({
      answered: false,
      answerSubmitted: null,
      collectionClozeSentences: withoutRequeuedCopy || collectionClozeSentences,
      correct: null,
      currentSentenceIndex: flashcardLastGrade.index,
      currentSentenceTimerStart: secondsSinceEpoch(),
      flashcardGradeHistory: flashcardGradeHistory.slice(0, -1),
      flashcardLaterGrades: flashcardUndoneGrade ? flashcardLaterGrades.concat([flashcardUndoneGrade]) : flashcardLaterGrades,
      flashcardRevealed: true,
      flashcardReturnFrom: flashcardLastGrade.correct ? "right" : "left",
      flashcardUndoneGrade: flashcardLastGrade,
      points: null,
      translationVisible: translationVisible || gameSettings.translations === 'show-after'
    });
  }

  // An undone card left without a regrade (an interval pick, Known, Ignore) keeps the grade it had.
  getKeptFlashcardGradeUpdates() {
    const { answered, flashcardGradeHistory, flashcardUndoneGrade } = this.state;
    if(!this.isPlayingFlashcard() || answered || !flashcardUndoneGrade) {
      return {};
    }

    return Object.assign(this.getFlashcardRegradeUpdates(flashcardUndoneGrade, flashcardUndoneGrade.correct), {
      flashcardGradeHistory: flashcardGradeHistory.concat([flashcardUndoneGrade])
    });
  }

  // The answer went to the server on the first grade, so a changed grade is corrected by an upsert.
  getFlashcardRegradeUpdates(undoneGrade, correct) {
    const { before, index } = undoneGrade;
    const { collectionClozeSentences } = this.state;
    let sentences = collectionClozeSentences;

    if(!correct) {
      const missedSentence = Object.assign({}, sentences[index], { wasIncorrect: true });
      sentences = sentences.map((s, i) => i === index ? missedSentence : s).concat([missedSentence]);
    }
    else if(!undoneGrade.correct) {
      sentences = sentences.map((s, i) => i === index ? Object.assign({}, s, { wasIncorrect: before.wasIncorrect }) : s);
    }

    if(correct === undoneGrade.correct) {
      return { collectionClozeSentences: sentences };
    }

    return Object.assign({ collectionClozeSentences: sentences }, this.flipFlashcardGrade(undoneGrade, correct));
  }

  flipFlashcardGrade(undoneGrade, correct) {
    const { before } = undoneGrade;
    const isFavorites = this.isFavoritedScope();
    const redo = this.isRedoRound();
    const levelPoints = (isCorrect) => redo
      ? { level: before.level, points: 0 }
      : getFlashcardLevelPoints({ correct: isCorrect, currentLevel: before.level, isFavorites, nextReview: before.nextReview });
    const previous = levelPoints(undoneGrade.correct);
    const { level, points } = levelPoints(correct);

    if(!redo) {
      const nextReview = isFavorites ? before.nextReview : moment().add(this.state.nextReviewByLevel[level], 'days').format("YYYY-MM-DD");
      this.updateSentence({ sentence: this.getCurrentSentence(), updates: { level, next_review: nextReview } });
    }

    const { correctlyAnsweredSentenceCount, numCorrect, numIncorrect, numMastered, score } = this.state;
    return flipAnswerResult(
      { correctlyAnsweredSentenceCount, numCorrect, numIncorrect, numMastered, points: this.state.points, score },
      { correct, currentLevel: before.level, level, points, previousLevel: previous.level, previousPoints: previous.points }
    );
  }

  onFlashcardClick(e) {
    if(e.target.closest("button, a, input")) {
      return;
    }
    this.flipFlashcard();
  }

  gradeFlashcard(result, input) {
    const { answered, currentSentenceIndex, flashcardFlyOut, flashcardRevealed, flashcardUndoneGrade } = this.state;
    if(!flashcardRevealed || answered || flashcardFlyOut) {
      return;
    }

    const correct = result === "got_it";
    const sentence = this.getCurrentSentence();
    const answer = correct ? this.getCurrentSentenceCloze() : "";

    if(correct) {
      this.playAnswerSoundEffect(true);
    }
    if(input === "swipe") {
      saveFlashcardSwipeCount(getFlashcardSwipeCount() + 1);
    }
    this.trackFlashcardEvent("flashcard_graded", Object.assign({ input, result }, flashcardUndoneGrade && { regraded: true }));

    if(flashcardUndoneGrade) {
      this.setState(Object.assign(this.getFlashcardRegradeUpdates(flashcardUndoneGrade, correct), {
        answered: true,
        answerSubmitted: answer,
        correct,
        flashcardGradeHistory: this.state.flashcardGradeHistory.concat([Object.assign({}, flashcardUndoneGrade, { correct })]),
        flashcardUndoneGrade: null
      }));
    }
    else {
      this.setState({
        flashcardGradeHistory: this.state.flashcardGradeHistory.concat([{
          before: { level: sentence.level, nextReview: sentence.nextReview, wasIncorrect: !!sentence.wasIncorrect },
          correct,
          index: currentSentenceIndex,
          sentenceId: sentence.id
        }])
      });
      this.submitAnswer(answer, { force: true });
    }
    this.flyOutFlashcard(correct ? "right" : "left");
  }

  flyOutFlashcard(direction) {
    this.setState({ flashcardFlyOut: direction });
  }

  flipFlashcard() {
    if(this.flashcardSwipeable) {
      this.flashcardSwipeable.startFlip();
    }
  }

  advanceAfterFlashcardGrade() {
    this.setState({ flashcardFlyOut: null });
    this.nextWithDefaultReview();
  }

  // Mirrors mobile's context overrides: the preview and the back face are this same card rendered from another state.
  renderWithStateOverrides(overrides, render) {
    const { state } = this;
    this.state = Object.assign({}, state, overrides);
    try {
      return render();
    }
    finally {
      this.state = state;
    }
  }

  renderFlashcardPreview() {
    const { collectionClozeSentences, currentSentenceIndex, gameSettings } = this.state;

    if(!collectionClozeSentences[currentSentenceIndex + 1]) {
      return null;
    }

    const isNextCardGraded = !!getLaterFlashcardGrade(this.state, currentSentenceIndex + 1);

    return this.renderWithStateOverrides({
      answered: false,
      correct: null,
      currentSentenceIndex: currentSentenceIndex + 1,
      flashcardRevealed: isNextCardGraded,
      multipleChoice: false,
      selectionPopoverId: null,
      sentenceTextVisible: false,
      tokensVisible: false,
      translationVisible: gameSettings.translations === 'visible' || (isNextCardGraded && gameSettings.translations === 'show-after'),
      usedHint: false
    }, () => this.renderFlashcardCard(" flashcard-preview"));
  }

  renderFlashcardBackFace() {
    const { gameSettings, translationVisible } = this.state;

    return this.renderWithStateOverrides({
      flashcardRevealed: true,
      selectionPopoverId: null,
      translationVisible: translationVisible || gameSettings.translations === 'show-after'
    }, () => this.renderFlashcardCard(" flashcard-back-face"));
  }

  renderFlashcard() {
    const { answered, currentSentenceIndex, flashcardFlyOut, flashcardRevealed, flashcardReturnFrom } = this.state;

    return (
      <FlashcardSwipeable
        backFace={flashcardRevealed ? null : this.renderFlashcardBackFace()}
        canFlip={!flashcardRevealed && !answered && !flashcardFlyOut && !this.isSentenceTextHidden()}
        canGrade={flashcardRevealed && !answered && !flashcardFlyOut}
        flyOut={flashcardFlyOut}
        isRevealed={flashcardRevealed}
        key={currentSentenceIndex}
        onDemoShown={() => this.trackFlashcardEvent("flashcard_swipe_demo_shown")}
        onFlipped={() => this.revealFlashcard()}
        onFlyOutDone={() => this.advanceAfterFlashcardGrade()}
        onSwipe={(result) => this.gradeFlashcard(result, "swipe")}
        preview={this.renderFlashcardPreview()}
        ref={(el) => this.flashcardSwipeable = el}
        returnFrom={flashcardReturnFrom}
      >
        {this.renderFlashcardCard()}
      </FlashcardSwipeable>
    );
  }

  renderFlashcardCard(extraClassName = "") {
    const { flashcardRevealed } = this.state;
    const showGradeCorners = flashcardRevealed && getFlashcardSwipeCount() < SWIPES_BEFORE_HIDING_GRADE_CORNERS;

    return (
      <div className={"flashcard" + (flashcardRevealed ? " revealed" : "") + extraClassName} onClick={(e) => this.onFlashcardClick(e)}>
        <div className="flashcard-top">
          <div className="flashcard-top-center">
            {flashcardRevealed ? this.renderLevelChecks() : <span className="flashcard-caption">Tap or swipe to reveal</span>}
          </div>
          {showGradeCorners && (
            <div className="flashcard-grade-corners">
              {this.renderFlashcardGradeCorner("missed")}
              {this.renderFlashcardGradeCorner("got_it")}
            </div>
          )}
        </div>
        <div className="flashcard-body">{this.renderCurrentSentence()}</div>
        {this.renderFlashcardControls()}
      </div>
    );
  }

  // Like mobile, the revealed controls keep their space from the start so nothing moves on reveal.
  renderFlashcardControls() {
    const { flashcardRevealed } = this.state;

    return (
      <div style={{ visibility: flashcardRevealed ? "visible" : "hidden" }}>
        {this.renderWithStateOverrides({ flashcardRevealed: true }, () => this.renderCurrentSentenceControls())}
      </div>
    );
  }

  renderFlashcardGradeCorner(grade) {
    const isGood = grade === "got_it";
    const label = isGood ? "Good" : "Again";

    return (
      <button
        aria-label={label}
        className={"flashcard-grade-corner joystix " + (isGood ? "flashcard-grade-corner-good" : "flashcard-grade-corner-again")}
        onClick={() => this.gradeFlashcard(grade, "button")}
      >
        {!isGood && <Icon name="arrow-left" type="fa" />}
        {label}
        {isGood && <Icon name="arrow-right" type="fa" />}
      </button>
    );
  }

  renderFlashcardBackButton() {
    const { answered, currentSentenceIndex, flashcardRevealed } = this.state;
    const isHideable = flashcardRevealed && !answered;
    const isDisabled = !isHideable && !canUndoFlashcardGrade(this.state, currentSentenceIndex);

    return (
      <button
        aria-label="flashcard back"
        className="btn btn-link flashcard-back"
        disabled={isDisabled}
        onClick={() => isHideable ? this.hideFlashcardAnswer() : this.undoFlashcardGrade()}
        title={isHideable ? "Hide answer" : "Back to the last card"}
      >
        <Icon name="undo" type="fa" />
      </button>
    );
  }

  // Both states share one grid cell, so the buttons sit in the same place before and after the reveal.
  renderFlashcardGradeButtons() {
    const { flashcardFlyOut, flashcardRevealed, gameSettings } = this.state;
    const hotkeysOn = gameSettings.hotkeys === "on";

    return (
      <div className="flashcard-grade-btns">
        <div className="flashcard-grade-btns-states">
          <div className="flashcard-grade-btns-state" style={{ visibility: flashcardRevealed ? "visible" : "hidden" }}>
            <div className="flashcard-explain-slot">{this.renderShowExplanationBtn()}</div>
            <div className="flashcard-grade-btns-row">
              <button aria-label="flashcard missed" className="btn btn-lg btn-default joystix" title={hotkeysOn ? "Again (←)" : "Again"} disabled={!flashcardRevealed || !!flashcardFlyOut} onClick={() => this.gradeFlashcard("missed", "button")}>
                Again
              </button>
              <button aria-label="flashcard got it" className="btn btn-lg btn-success joystix" title={hotkeysOn ? "Good (Enter / →)" : "Good (Enter)"} disabled={!flashcardRevealed || !!flashcardFlyOut} onClick={() => this.gradeFlashcard("got_it", "button")}>
                Good
              </button>
            </div>
          </div>
          <div className="flashcard-grade-btns-state" style={{ visibility: flashcardRevealed ? "hidden" : "visible" }}>
            <button aria-label="reveal flashcard" className="btn btn-lg btn-success btn-block joystix" title={hotkeysOn ? "Reveal (Enter / Space)" : "Reveal (Enter)"} disabled={flashcardRevealed} onClick={() => this.flipFlashcard()}>
              Reveal
            </button>
          </div>
        </div>
      </div>
    );
  }

  onMultipleChoiceOptionClick(answer) {
    if(this.state.answered) {
      this.next();
    }
    else {
      this.submitAnswer(answer);
    }
  }

  renderMultipleChoiceOptions() {
    if(this.isPlayingSpeaking() && !this.state.multipleChoice) {
      return null;
    }

    if(!this.isPlayingMultipleChoice() && !this.state.multipleChoice) {
      return null;
    }

    const { answered, answerSubmitted, currentSentenceIndex, correct, wordBank } = this.state;
    const currentSentenceMultipleChoiceOptions = this.getCurrentSentence().multipleChoiceOptions;
    const options =  currentSentenceMultipleChoiceOptions.length ? currentSentenceMultipleChoiceOptions : wordBank

    return (
      <MultipleChoiceOptions
        actualAnswer={this.getCurrentSentenceCloze()}
        answered={answered}
        answerSubmitted={answerSubmitted}
        correct={correct}
        onOptionClick={(o) => this.onMultipleChoiceOptionClick(o)}
        onClickableOptionClick={(o) => this.setState({
          selectionPopoverId: "sentence",
          selectedSentenceText: o
        })}
        options={options}
        ref={(el) => this.multipleChoiceOptions = el}
        sentenceIndex={currentSentenceIndex}
        targetLanguageCode={this.props.targetLanguageCode}
      />
    );
  }

  renderListeningControl() {
    const { skill } = this.props;
    const { currentSentenceIndex, gameSettings } = this.state;

    if(!this.isPlayingListening()) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();

    return (
      <ListeningControl
        key={currentSentenceIndex}
        onPlayComplete={() => this.setState({ listeningControlPlayComplete: true }, () => this.textInput && this.textInput.focus())}
        onError={() => this.setState({ listeningControlPlayComplete: true }, () => this.textInput && this.textInput.focus())}
        playAudio={this.playSentenceAudio.bind(this)}
        playCompleteOnFirstPlayEnded={gameSettings.listeningSkillAutoShowSentence === 'on'}
      />
    );
  }

  startRecording() {
    if(this.state.recording) {
      return false;
    }
    this.setState({ recording: true, speakingRecordingSeconds: 0, speakingAutoStopNoticeVisible: false });
    try {
      this.startLocalRecording();
      if(this.recordingTimer) {
        clearInterval(this.recordingTimer);
      }
      const recordingStartedAt = Date.now();
      this.recordingTimer = setInterval(() => {
        const speakingRecordingSeconds = Math.floor((Date.now() - recordingStartedAt) / 1000);
        if(speakingRecordingSeconds !== this.state.speakingRecordingSeconds) {
          this.setState({ speakingRecordingSeconds });
        }
      }, 250);
      this.recognition.start();
      this.recognitionTimeout = setTimeout(() => {
        this.stopRecordingAndResetRecordBtn({ autoStopped: true });
      }, 10000); // 10s record limit
    } catch(e) {
      this.onSpeechRecognitionError(e);
    }
  }

  stopRecordingAndResetRecordBtn({ autoStopped = false } = {}) {
    if(this.recognition) {
      this.recognition.stop();
    }
    this.stopLocalRecording();
    this.setState({
      recording: false,
      speakingRecordingSeconds: 0,
      speakingAutoStopNoticeVisible: autoStopped
    });
    if(this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    clearTimeout(this.recognitionTimeout);
    if(autoStopped) {
      clearTimeout(this.speakingAutoStopNoticeTimeout);
      this.speakingAutoStopNoticeTimeout = setTimeout(() => {
        this.setState({ speakingAutoStopNoticeVisible: false });
      }, 2500);
    }
  }


  renderSpeakingControl() {
    if(!this.isPlayingSpeaking()) {
      return null;
    }

    const {
      answered,
      recording,
      speakingAnswerCheckMode,
      sentenceTextVisible,
      speakingRecordingSeconds,
      speakingTranscript,
      speakingAutoStopNoticeVisible,
      speakingRecordingUrl
    } = this.state;

    return (
      <div style={{ marginTop: 10 }}>
        {!answered && (
          <div style={{ alignItems: "center", display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              className={`btn btn-lg speaking-record-btn ${recording ? "btn-danger speaking-recording" : "btn-default"}`}
              onClick={() => (recording ? this.stopRecordingAndResetRecordBtn() : this.startRecording())}
            >
              <span className="glyphicon glyphicon-record speaking-record-icon" style={{ color: recording ? "#fff" : "#f00", fontSize: 100, padding: "20px 32px" }}></span><br />{recording ? "Stop" : "Record"}
            </button>
            <div className="speaking-answer-check-toggle">
              <span className="control-label">Check answer</span>
              <div className="btn-group">
                <button
                  type="button"
                  className={`btn btn-default btn-xs ${speakingAnswerCheckMode === "live" ? "active" : ""}`}
                  onClick={() => {
                    window.localStorage.setItem("speakingAnswerCheckMode", "live");
                    this.setState({ speakingAnswerCheckMode: "live" });
                  }}
                >
                  As I speak
                </button>
                <button
                  type="button"
                  className={`btn btn-default btn-xs ${speakingAnswerCheckMode === "final" ? "active" : ""}`}
                  onClick={() => {
                    window.localStorage.setItem("speakingAnswerCheckMode", "final");
                    this.setState({ speakingAnswerCheckMode: "final" });
                  }}
                >
                  When I stop
                </button>
              </div>
            </div>
            {!this.state.multipleChoice && (
              <button
                className="btn btn-default"
                onClick={() => this.setState({ multipleChoice: true })}
              >
                <Icon name="th-large" /> Show options
              </button>
            )}
          </div>
        )}
        {answered && speakingRecordingUrl && (
          <div className="speaking-compare" style={{ textAlign: "center" }}>
            <strong>Compare:</strong>
            <audio controls src={speakingRecordingUrl} />
            <button
              className={`btn btn-xs speaking-record-btn ${recording ? "btn-danger speaking-recording" : "btn-default"}`}
              onClick={() => (recording ? this.stopRecordingAndResetRecordBtn() : this.startRecording())}
            >
              <span className="glyphicon glyphicon-record speaking-record-icon" style={{ color: recording ? "#fff" : "#f00" }}></span> {recording ? "Stop" : "Re-record"}
            </button>
          </div>
        )}
        {recording && (
          <div className="speaking-recording-status">
            Recording… {speakingRecordingSeconds}s
          </div>
        )}
        {speakingAutoStopNoticeVisible && (
          <div className="speaking-auto-stop-notice">
            Recording stopped automatically.
          </div>
        )}
        <div style={{ marginTop: 6 }}>{speakingTranscript}</div>
      </div>
    );
  }

  renderRoundResults() {
    if(this.props.landing && !this.props.isSignedIn) {
      // A missed sentence comes back later in the round, so count first tries per sentence.
      const missedIds = new Set(this.state.collectionClozeSentences.filter((ccs) => ccs.wasIncorrect).map((ccs) => ccs.id));
      const sentenceIds = new Set(this.state.collectionClozeSentences.map((ccs) => ccs.id));
      return (
        <LandingSignUp
          amplitudeRoundProperties={this.amplitudeRoundProperties()}
          landing={this.props.landing}
          missedIds={missedIds}
          numCorrect={sentenceIds.size - missedIds.size}
          numSentences={sentenceIds.size}
          sentences={this.state.collectionClozeSentences.filter((ccs, i, all) => all.findIndex((other) => other.id === ccs.id) === i)}
        />
      );
    }

    const {
      collectionClozeSentences,
      collectionClozeSentencesUpsertUrl,
      collectionsUrl,
      elapsedTime,
      hasShownTokens,
      isCollectionEditable,
      isPro,
      languagePairingUrl,
      leveledUp,
      nextReviewByLevel,
      numCorrect,
      numIncorrect,
      numMastered,
      numNew,
      numReview,
      onboarding,
      reportErrorUrl,
      startingNumMastered,
      startingNumPlaying,
      startingNumReadyForReview,
      score,
      tokensUrl,
      updating
    } = this.state;
    const {
      baseLanguageEnglishName,
      baseLanguageFlagIso,
      collectionId,
      isTeachingCollection,
      languagePairingWebUrl,
      scope,
      targetLanguageCode,
      targetLanguageEnglishName,
      targetLanguageFlagIso,
      targetLanguageIso,
      targetLanguageName,
      teachingCollectionResultsUrl
    } = this.props;
    const uniqueSentences = [];
    const uniqueSentenceIds = {};
    for(let i = 0, n = collectionClozeSentences.length; i < n; i++) {
      if(!uniqueSentenceIds[collectionClozeSentences[i].id]) {
        uniqueSentences.push(collectionClozeSentences[i]);
        uniqueSentenceIds[collectionClozeSentences[i].id] = true;
      }
    }
    return (
      <SlideDown>
        <RoundResults
          amplitudeRoundProperties={this.amplitudeRoundProperties()}
          answerTape={collectionClozeSentences.map((ccs) => ({ id: ccs.id, correct: !ccs.wasIncorrect }))}
          baseLanguageEnglishName={baseLanguageEnglishName}
          baseLanguageFlagIso={baseLanguageFlagIso}
          collectionClozeSentences={uniqueSentences}
          collectionClozeSentencesUpsertUrl={collectionClozeSentencesUpsertUrl}
          collectionId={collectionId}
          collectionsUrl={collectionsUrl}
          elapsedTime={elapsedTime}
          hasShownTokens={hasShownTokens}
          isCollectionEditable={isCollectionEditable}
          isPro={isPro}
          isSignedIn={this.props.isSignedIn}
          isTeachingCollection={isTeachingCollection}
          isTtsAvailable={this.isTtsAvailable.bind(this)}
          languagePairings={this.props.languagePairings}
          languagePairingUrl={languagePairingUrl}
          shouldUseFreeLeftTodayPromo={this.props.shouldUseFreeLeftTodayPromo}
          leveledUp={leveledUp}
          maxPlayableToday={this.state.maxPlayableToday}
          nextReviewByLevel={nextReviewByLevel}
          numCorrect={numCorrect}
          numIncorrect={numIncorrect}
          roundNumGaveUp={this.state.roundNumGaveUp}
          roundNumRetried={this.state.roundNumRetried}
          roundNumSubmits={this.state.roundNumSubmits}
          numMastered={numMastered}
          numNew={numNew}
          numPlayedToday={this.state.numPlayedToday}
          numReview={numReview}
          onNextRound={() => {
            if(onboarding) {
              this.setState({ onboardingNextOptionsVisible: true }, () => $("html, body").animate({ scrollTop: 0 }));
            }
            else {
              this.nextRound();
            }
          }}
          playSoundEffect={() => {
            if(this.state.gameSettings.soundEffects === 'on' && this.props.roundCompleteAudioUrl) {
              const a = new Audio(this.props.roundCompleteAudioUrl);
              a.volume = 0.25;
              a.play();
            }
          }}
          onboarding={onboarding}
          onRedoRound={() => this.startRedoRound("button")}
          playTts={this.playSentenceAudio.bind(this)}
          redoable={this.isRedoAvailable()}
          redoMode={this.isRedoRound()}
          ref={(el) => this.roundResults = el}
          reportErrorUrl={reportErrorUrl}
          scope={scope}
          score={score}
          startingNumMastered={startingNumMastered}
          startingNumPlaying={startingNumPlaying}
          startingNumReadyForReview={startingNumReadyForReview}
          targetLanguageCode={targetLanguageCode}
          targetLanguageEnglishName={targetLanguageEnglishName}
          targetLanguageFlagIso={targetLanguageFlagIso}
          targetLanguageIso={targetLanguageIso}
          targetLanguageName={targetLanguageName}
          teachingCollectionResultsUrl={teachingCollectionResultsUrl}
          tokensUrl={tokensUrl}
          updateHasShownTokens={() => this.setState({ hasShownTokens: true })}
          updateSentence={this.updateSentence.bind(this)}
          updating={updating}
        />
        {this.renderFavoriteProPromoModal()}
      </SlideDown>
    );
  }

  renderFavoriteProPromoModal() {
    if(this.state.isPro) {
      return null;
    }

    return (
      <Modal
        id="favorite-pro-promo-modal"
        show={false}
        title="Favorite"
      >
        <ModalProPromo
          can="favorite sentences and then practice just their favorites or download their favorites (to print out, for import into Anki, etc.)."
          secondary="Take control of your learning. Get fluent faster."
        />
      </Modal>
    );
  }

  onKnowItClick() {
    if(!this.progressControlsEnabled()) {
      return false;
    }
    if(this.knowItDontShowAgain && this.gameSettings) {
      this.gameSettings.updateSetting('manualMasterResetConfirm', 'off');
    }
    this.knowItModal && this.knowItModal.hide();
    const currentSentence = this.getCurrentSentence();
    this.updateSentence({
      callback: () => this.next(),
      sentence: currentSentence,
      updates: {
        level: 4,
        next_review: "2100-01-01"
      }
    });
  }

  renderKnowItModal() {
    return (
      <Modal
        id="know-it-modal"
        onShown={() => this.knowItModal.$modal.find('.btn').focus()}
        ref={(el) => this.knowItModal = el}
        show={false}
        size="small"
        title="Known"
      >
        <h4 className="text-center" style={{ marginTop: 0 }}>Feeling confident?</h4>
        <p className="text-center">This will set the sentence to 100% Mastered and no more reviews.</p>
        <p>
          <button className="btn btn-success joystix btn-block go" onClick={() => this.onKnowItClick()}>Mark as Known</button>
        </p>
        <div className="checkbox text-center" style={{ marginTop: 8 }}>
          <label>
            <input type="checkbox" onChange={(e) => this.knowItDontShowAgain = e.target.checked} /> Don't show this again
          </label>
          <br />
          <small>(you can toggle this in the game settings)</small>
        </div>
      </Modal>
    );
  }

  onMasterClick() {
    if(!this.progressControlsEnabled()) {
      return false;
    }
    this.masterModal.hide();
    this.updateCurrentSentence('level', 4);
  }

  renderMasterModal() {
    const { nextReviewByLevel } = this.state;
    let nextReview = moment().add(nextReviewByLevel[4], 'days').format("YYYY-MM-DD");
    if(nextReview > "2100-01-01") {
      nextReview = "the year 2100";
    }

    return (
      <Modal
        id="master-modal"
        onShown={() => this.masterModal.$modal.find('.btn').focus()}
        ref={(el) => this.masterModal = el}
        show={false}
        size="small"
        title="Master"
      >
        <h4 className="text-center" style={{ marginTop: 0 }}>Already know this sentence?</h4>
        <p>
          <button className="btn btn-success joystix btn-block go" onClick={() => this.onMasterClick()}>Set to 100% Mastered</button>
        </p>
        <div className="text-center">Next review due date will be {nextReview}.</div>
      </Modal>
    );
  }

  onResetClick() {
    if(!this.progressControlsEnabled()) {
      return false;
    }
    this.resetModal.hide();
    this.updateCurrentSentence('level', 0);
  }

  renderResetModal() {
    const { nextReviewByLevel } = this.state;
    let nextReview = moment().add(nextReviewByLevel[0], 'days').format("YYYY-MM-DD");

    return (
      <Modal
        id="reset-modal"
        onShown={() => this.resetModal.$modal.find('.btn').focus()}
        ref={(el) => this.resetModal = el}
        show={false}
        size="small"
        title="Reset"
      >
        <h4 className="text-center" style={{ marginTop: 0 }}>Want to see this sentence again sooner?</h4>
        <p>
          <button className="btn btn-success joystix btn-block go" onClick={() => this.onResetClick()}>Reset to 0% Mastered</button>
        </p>
        <div className="text-center">Next review due date will be {nextReview}.</div>
      </Modal>
    );
  }

  popLastSentenceIfIgnoredAndIncorrect(callback) {
    const { collectionClozeSentences } = this.state;
    const lastSentence = collectionClozeSentences[collectionClozeSentences.length - 1];
    const currentSentence = this.getCurrentSentence();

    // when incorrect the sentence is pushed onto collectionClozeSentences
    // when we ignore it, we want to pop it off so it isn't played again
    if(lastSentence.id === currentSentence.id && lastSentence.wasIncorrect && lastSentence.ignored) {
      collectionClozeSentences.pop();
    }

    this.setState({
      collectionClozeSentences: JSON.parse(JSON.stringify(collectionClozeSentences))
    }, callback);
  }

  onIgnoreAllClick() {
    this.ignoreModal.hide();
    this.ignoreAll(() => this.popLastSentenceIfIgnoredAndIncorrect(() => this.next()));
  }

  onIgnoreClick() {
    this.ignoreModal.hide();
    this.updateCurrentSentence("ignored", true, () => {
      this.popLastSentenceIfIgnoredAndIncorrect(() => this.next());
    });
  }

  renderIgnoreModal() {
    const { pro } = this.state;

    return (
      <Modal
        id="ignore-modal"
        onShown={() => this.ignoreModal.$modal.find('.btn').first().focus()}
        ref={(el) => this.ignoreModal = el}
        show={false}
        size="small"
        title="Ignore"
      >
        <p>
          Ignoring a sentence removes it from your queue. You'll no longer see it and it won't count towards your progress.
          <button className="btn btn-danger btn-block joystix ignore-sentence" style={{ marginTop: 5 }} onClick={() => this.onIgnoreClick()}>Ignore</button>
        </p>
        {!!this.props.collectionId && (
          <p>
            Or you can ignore all sentences with the same missing word.
            <button className="btn btn-danger btn-block joystix ignore-all" onClick={() => this.onIgnoreAllClick()} style={{ marginTop: 5 }}>Ignore All</button>
          </p>
        )}
        {/*
        <p>
          <strong>If there's an error please report it by clicking the flag button bottom right.</strong>
        </p>
        */}
        {!pro && (
          <small>
            <strong><a href="/pro">Clozemaster Pro</a> subscribers can unignore sentences as well as search all sentences!</strong>
          </small>
        )}
      </Modal>
    );
  }

  renderGameSettingsControl() {
    return (
      <button className="btn btn-default game-settings" data-toggle="modal" data-target="#game-settings-modal">
        <span className="glyphicon glyphicon-cog"></span> Settings
      </button>
    );
  }

  renderExplanationModal() {
    const { explanationModalVisible, isPro, showExplanations } = this.state;

    if(!showExplanations || !explanationModalVisible) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();

    return (
      <Modal
        footer={<ModalFooterCloseBtn />}
        id="explanation-modal"
        onHidden={() => this.setState({ explanationModalVisible: false })}
        show={explanationModalVisible}
        title="Explanation"
      >
        {/*
        <div style={{ whiteSpace: "pre-wrap" }}>{currentSentence.explanation}</div>
        */}
        <CollectionClozeSentenceExplanation
          collectionClozeSentence={currentSentence}
          isPro={isPro}
          key={currentSentence.id}
          onExplanation={(explanation) => this.setState({ collectionClozeSentences: this.state.collectionClozeSentences.map((ccs) => ccs.id === currentSentence.id ? { ...ccs, explanation } : ccs) })}
          test={this.props.test}
        />
      </Modal>
    );
  }

  renderGameSettingsModal() {
    return (
      <Modal
        footer={<ModalFooterCloseBtn />}
        id="game-settings-modal"
        show={false}
        title="Game Settings"
      >
        <GameSettings
          amplitudeProperties={this.amplitudeRoundProperties()}
          audioRecordingsAvailable={this.audioRecordingsAvailable()}
          clozemasterTtsAvailable={!!this.getCurrentSentence().ttsAudioUrl}
          initialSettings={this.state.gameSettings}
          isPlayingFullTextInput={this.isPlayingFullTextInput()}
          isPlayingListening={this.isPlayingListening()}
          isPlayingMultipleChoice={this.isPlayingMultipleChoice()}
          isPlayingSpeaking={this.isPlayingSpeaking()}
          isTranslate={this.isPlayingTranslate()}
          isSignedIn={this.props.isSignedIn}
          shortcutsMode={this.shortcutsMode()}
          onUpdate={(gameSettings) => {
            let translationVisible = this.state.translationVisible;

            if(gameSettings.translations === 'show-after' && !this.state.answered || gameSettings.translations === 'hidden') {
              translationVisible = false;
            }

            this.setState({
              gameSettings,
              translationVisible
            });
          }}
          ref={(el) => this.gameSettings = el}
          strictDiacriticsCopy={this.strictDiacriticsCopy()}
          systemTtsAvailable={this.isSystemTtsAvailable()}
          systemTtsVoices={this.getSystemTtsVoices()}
          targetLanguageName={this.props.targetLanguageName}
          transliterationsAvailable={this.transliterationsAvailable()}
          url={this.state.gameSettingsUrl}
        />
      </Modal>
    );
  }

  transliterationsAvailable() {
    const { collectionClozeSentences } = this.state;
    return !!collectionClozeSentences.find((ccs) => !!ccs.transliteration);
  }

  audioRecordingsAvailable() {
    const { collectionClozeSentences } = this.state;
    return !!collectionClozeSentences.find((ccs) => !!ccs.audioRecordingUrl);
  }

  onTeachMeClick() {
    const cloze = this.getCurrentSentenceCloze();
    this.setState({ teachMeUsed: true, textInputValue: '' }, () => {
      if(this.textInput) {
        this.textInput.focus();
      }
    });
  }

  renderTextInputSubmitBtn() {
    if(this.isPlayingSpeaking()) {
      return null;
    }

    if(this.isPlayingTextInput() && !this.state.multipleChoice && !this.state.answered) {
      const { teachMeUsed, textInputValue } = this.state;
      return (
        <div style={{ alignItems: "center", display: "flex", flexDirection: "column", gap: 6 }}>
          <button className="btn btn-success joystix btn-lg" onClick={() => this.submitAnswer(textInputValue)} style={{ width: 140 }} disabled={!textInputValue}>Submit</button>
          {!teachMeUsed && <button className="btn btn-success-outline joystix btn-sm" onClick={() => this.onTeachMeClick()}>Teach Me</button>}
        </div>
      );
    }
  }

  renderSoundEffects() {
    const {
      correctAudioUrl,
      incorrectAudioUrl,
    } = this.props;

    return (
      <div className="audio">
        <audio ref={(el) => this.correctAudio = el} src={correctAudioUrl} />
        <audio ref={(el) => this.incorrectAudio = el} src={incorrectAudioUrl} />
      </div>
    );
  }

  getUpdatedCollectionClozeSentences(id, updates) {
    const newCollectionClozeSentences = JSON.parse(JSON.stringify(this.state.collectionClozeSentences));
    for(let i = 0, n = newCollectionClozeSentences.length; i < n; i++) {
      if(newCollectionClozeSentences[i].id === id) {
        newCollectionClozeSentences[i] = Object.assign(newCollectionClozeSentences[i], updates);
      }
    }
    return newCollectionClozeSentences;
  }

  onSentenceEditorUpdate(sentence) {
    this.sentenceEditorModal && this.sentenceEditorModal.hide();
    const currentSentence = this.getCurrentSentence();
    const newCollectionClozeSentences = this.getUpdatedCollectionClozeSentences(currentSentence.id, sentence);
    this.setState({ collectionClozeSentences: newCollectionClozeSentences, textInputChangedAfterAnswering: sentence.text !== currentSentence.text });
  }

  addCurrentSentenceToCollection() {
    const currentSentence = this.getCurrentSentence();
    this.setState({
      addSentenceToCollectionModalVisible: true
    });
  }

  renderAddSentenceToCollectionModal() {
    if(!this.state.addSentenceToCollectionModalVisible) {
      return null;
    }

    const { isPro, nextReviewByLevel, selectedSentenceClozeStr, selectedSentenceText } = this.state;
    const sentence = Object.assign({}, this.getCurrentSentence());
    delete sentence.id;
    let copyable = true;

    if(selectedSentenceClozeStr && selectedSentenceClozeStr.match(/.*\{\{.+\}\}.*/)) { // if present and contains cloze
      sentence.text = selectedSentenceClozeStr;
      copyable = false;
    }
    else if(selectedSentenceText) {
      sentence.text = sentence.text.replace(/{{|}}/g, '');
      sentence.text = sentence.text.replace(selectedSentenceText, '{{' + selectedSentenceText + '}}');
      copyable = false;
    }

    return (
      <AddSentenceToCollectionModal
        collectionClozeSentence={sentence}
        collectionsUrl={this.state.collectionsUrl}
        copyable={copyable}
        isPro={isPro}
        nextReviewByLevel={nextReviewByLevel}
        onHidden={() => this.setState({
          addSentenceToCollectionModalVisible: false,
          selectedSentenceClozeStr: null, // clear cloze str
          selectedSentenceText: null // clear selected text
        })}
        onSentenceAdded={(sentence) => this.addSentenceToCollectionModal && this.addSentenceToCollectionModal.hide()}
        ref={(el) => this.addSentenceToCollectionModal = el}
      />
    );
  }

  renderSentenceEditorModal() {
    if(!this.state.sentenceEditorModalVisible) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();

    return (
      <CollectionClozeSentenceEditorModal
        alreadyMasteredNextReview={this.state.alreadyMasteredNextReview}
        answerQualityOptionsVisible={this.answerQualityOptionsVisible()}
        isPro={this.state.isPro}
        isTextEditable={this.state.isCollectionEditable}
        nextReviewByLevel={this.state.nextReviewByLevel}
        onHidden={() => this.setState({ sentenceEditorModalVisible: false })}
        onUpdate={(sentence) => this.onSentenceEditorUpdate(sentence)}
        ref={(el) => this.sentenceEditorModal = el}
        sentence={currentSentence}
        upsertUrl={currentSentence.collectionClozeSentencesUpsertUrl || this.state.collectionClozeSentencesUpsertUrl}
      />
    );
  }

  renderLeveledUpModal() {
    if(!this.state.leveledUpModalVisible) {
      return null;
    }

    const {
      baseLanguageEnglishName,
      baseLanguageFlagIso,
      leveledUpImageUrl,
      leveledUpShareUrl,
      targetLanguageEnglishName,
      targetLanguageFlagIso
    } = this.props;

    return (
      <LeveledUpModal
        baseLanguageEnglishName={baseLanguageEnglishName}
        baseLanguageFlagIso={baseLanguageFlagIso}
        imagePath={leveledUpImageUrl}
        level={this.state.languagePairingLevel}
        onHidden={() => this.setState({ leveledUpModalVisible: false })}
        shareUrl={leveledUpShareUrl}
        targetLanguageEnglishName={targetLanguageEnglishName}
        targetLanguageFlagIso={targetLanguageFlagIso}
      />
    );
  }

  renderManageCollectionModal() {
    if(!this.state.manageCollectionModalVisible) {
      return null;
    }

    const {
      baseLanguageEnglishName,
      targetLanguageCode,
      targetLanguageEnglishName,
      targetLanguageIso,
      targetLanguageName
    } = this.props;

    return (
      <ManageCollectionModal
        baseLanguageEnglishName={baseLanguageEnglishName}
        collectionClozeSentencesUrl={this.state.manageCollectionCollectionClozeSentencesUrl || this.state.collectionClozeSentencesUrl}
        collectionsUrl={this.state.collectionsUrl}
        isPro={this.state.isPro}
        onHidden={() => this.setState({
          manageCollectionCollectionClozeSentencesUrl: null,
          manageCollectionModalVisible: false,
          manageCollectionQuery: ""
        })}
        onUpdate={(updatedCollectionClozeSentences) => {
          const { collectionClozeSentences } = this.state;
          const updatedHash = updatedCollectionClozeSentences.reduce((h, ccs) => {
            h[ccs.id] = ccs;
            return h;
          }, {});
          const newCollectionClozeSentences = [];
          for(let i = 0, n = collectionClozeSentences.length; i < n; i++) {
            newCollectionClozeSentences.push(
              Object.assign({}, collectionClozeSentences[i], updatedHash[collectionClozeSentences[i].id] || {})
            );
          }
          this.setState({ collectionClozeSentences: newCollectionClozeSentences });
        }}
        query={this.state.selectedSentenceText || this.state.manageCollectionQuery}
        targetLanguageCode={targetLanguageCode}
        targetLanguageEnglishName={targetLanguageEnglishName}
        targetLanguageIso={targetLanguageIso}
        targetLanguageName={targetLanguageName}
      />
    );
  }

  isListening() {
    const { skill } = this.props;
    const { listeningControlPlayComplete } = this.state;
    return this.isPlayingListening() && !listeningControlPlayComplete;
  }

  // The single place that decides whether the "initially hide sentence text" setting is
  // in play. Never in full text input: the sentence is already masked there, so
  // honouring it would hide renderGameControls (Submit, Next, No idea) and hand the
  // first Enter to showSentenceText instead of submitting.
  //
  // This lives apart from isSentenceTextHidden because the hotkey handlers need the same
  // question answered without the !answered term - the space-key branch is reachable
  // after answering. Both used to re-implement the condition inline, which is how the
  // full-text-input exemption missed them: the render path honoured it and Enter did
  // not, so the first Enter of a Translate round was silently swallowed.
  sentenceTextInitiallyHiddenApplies() {
    const { gameSettings, sentenceTextVisible } = this.state;
    return !this.isPlayingFullTextInput() &&
      gameSettings.sentenceTextInitiallyHidden === "on" &&
      !sentenceTextVisible;
  }

  isSentenceTextHidden() {
    return !this.state.answered && this.sentenceTextInitiallyHiddenApplies();
  }

  // "Write it" / "Speak it" after answering. Opt-in for now: only shown when the setting is
  // turned on, so a missing setting counts as off.
  morePracticeControlsEnabled() {
    return this.state.gameSettings.morePracticeControls === "on";
  }

  renderMorePracticeControls() {
    if(!this.state.answered || !this.morePracticeControlsEnabled()) {
      return null;
    }

    // the open next review controls sit right above, with no gap of their own
    return (
      <div style={{ marginTop: this.nextReviewControlsShown() ? 15 : 0 }}>
        <MorePracticeControls
          hotkeysEnabled={this.state.gameSettings.hotkeys === "on"}
          playSentenceAudio={this.playSentenceAudio.bind(this)}
          ref={(el) => { this.morePracticeControls = el; }}
          sentence={this.getCurrentSentence()}
          // the whole sentence was just typed out, so writing it again is no extra practice
          writeItAvailable={!this.isPlayingFullTextInput()}
        />
      </div>
    );
  }

  renderGameControls() {
    if(this.isListening()) {
      return null;
    }

    if(this.isSentenceTextHidden()) {
      return null;
    }

    if(this.isPlayingFlashcard()) {
      return <FadeIn>{this.renderFlashcardGradeButtons()}</FadeIn>;
    }

    return (
      <FadeIn>
        {this.renderTextInputSubmitBtn()}
        {this.renderMorePracticeControls()}
        {this.renderNextBtn()}
        {this.renderMultipleChoiceOptions()}
        {this.renderDontKnowBtn()}
      </FadeIn>
    );
  }

  renderDontKnowBtn() {
    const { level } = this.getCurrentSentence();
    const { answered } = this.state;
    const { dontKnowBtnEnabled } = this.props;

    if(!dontKnowBtnEnabled || answered) {
      return null;
    }

    return (
      <div style={this.isPlayingTextInput()  || this.isPlayingFullTextInput() ? { marginTop: 10 } : null}>
        <button className="btn btn-default btn-xs" onClick={this.onDontKnowClick.bind(this)}>No idea ¯\_(ツ)_/¯</button>
      </div>
    );
  }

  onDontKnowClick() {
    const cloze = this.getCurrentSentenceCloze();
    const { textInputValue } = this.state;

    // setting previous answer to skip off by hint
    this.setState({ previousAnswer: "" }, () => {
      this.submitAnswer(textInputValue || "", { force: true });
      this.setState({
        selectionPopoverId: "sentence",
        selectedSentenceText: cloze
      });
    });
  }

  renderClozeSentenceSearchModal() {
    if(!this.state.clozeSentenceSearchModalVisible) {
      return null;
    }

    return (
      <ClozeSentenceSearchModal 
        collectionsUrl={this.state.collectionsUrl}
        isPro={this.state.isPro}
        nextReviewByLevel={this.state.nextReviewByLevel}
        onHidden={() => {
          this.setState({ clozeSentenceSearchModalVisible: false });
        }}
        query={this.state.selectedSentenceText}
        url={this.state.clozeSentencesUrl}
      />
    );
  }

  renderDiscussionModal() {
    const { discussionModalVisible } = this.state;

    if(!discussionModalVisible) {
      return null;
    }

    const { baseLanguageEnglishName, targetLanguageEnglishName, targetLanguageName } = this.props;
    const currentSentence = this.getCurrentSentence();
    const { translation } = currentSentence;

    return (
      <DiscussionModal
        {...this.getCurrentSentenceParts()}
        baseLanguageEnglishName={baseLanguageEnglishName}
        isPro={this.state.isPro}
        onHidden={() => this.setState({ discussionModalVisible: false })}
        sentencePlainText={this.getCurrentSentencePlainText()}
        translation={translation}
        targetLanguageEnglishName={targetLanguageEnglishName}
        targetLanguageName={targetLanguageName}
      />
    );
  }

  renderRoundHistoryModal() {
    const { answered, collectionClozeSentences, currentSentenceIndex, isPro, roundHistoryModalVisible } = this.state;

    if(!roundHistoryModalVisible) {
      return null;
    }

    return (
      <RoundHistoryModal
        collectionClozeSentences={collectionClozeSentences.slice(0, currentSentenceIndex + (answered ? 1 : 0))}
        isPro={isPro}
        onEditClick={(collectionClozeSentence) => {
          this.setState({
            manageCollectionCollectionClozeSentencesUrl: collectionClozeSentence.collectionClozeSentencesUrl,
            manageCollectionModalVisible: true,
            manageCollectionQuery: this.getSentencePlainText(collectionClozeSentence.text)
          });
        }}
        onHidden={() => this.setState({ roundHistoryModalVisible: false })}
      />
    );
  }

  renderResourceLinksEditorModal() {
    const { isPro, resourceLinksEditorVisible, resourceLinksUrl } = this.state;

    return (
      <Modal
        onHidden={() => this.setState({ resourceLinksEditorVisible: false })}
        show={!!resourceLinksEditorVisible}
        title="Resource Links"
      >
        {resourceLinksEditorVisible && (
          <ResourceLinksEditor
            isPro={isPro}
            onCancel={() => this.setState({ resourceLinksEditorVisible: false })}
            onUpdate={() => this.setState({ resourceLinksEditorVisible: false })}
            resourceLinksUrl={resourceLinksUrl}
          />
        )}
      </Modal>
    );
  }

  renderHelpModal() {
    return (
      <HelpModal
        accentMap={LANGUAGE_ACCENT_MAP[this.props.targetLanguageIso]}
        isPlayingTextInput={this.isPlayingTextInput()}
        isUsingSrsControls={!!this.state.alreadyMasteredNextReview}
        mode={this.shortcutsMode()}
        runTutorial={this.runTutorial.bind(this)}
      />
    );
  }

  renderCurrentSentenceImageModal() {
    const { currentSentenceImageModalVisible } = this.state;

    if(!currentSentenceImageModalVisible) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();
    const { answered } = this.state;

    return (
      <Modal
        footer={(
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <small>Images are in beta. Like them? Hate them? <a href="/contact" target="_blank">Let us know</a>!</small>
            <ModalFooterCloseBtn />
          </div>
        )}
        onHidden={() => this.setState({ currentSentenceImageModalVisible: false })}
        ref={(el) => this.currentSentenceImageModal = el}
        show={!!currentSentenceImageModalVisible}
        title="Image"
      >
        {answered && (
          <p>
            <ClozeSentence text={currentSentence.text} />
            <br />
            {currentSentence.translation}
          </p>
        )}
        <p className="text-center">
          <img src={currentSentence.imageUrl} style={{ maxWidth: "100%" }} />
        </p>
        <div>
          <small>Change when and how images are shown in the game settings. </small>
          <button
            className="btn btn-xs btn-default game-settings"
            onClick={() => {
              this.currentSentenceImageModal.hide();
              $("#game-settings-modal").modal("show");
            }}
          >
            <Icon name="cog" />
          </button>
        </div>
      </Modal>
    );
  }

  renderModals() {
    return (
      <>
        {this.renderAddSentenceToCollectionModal()}
        {this.renderClozeSentenceSearchModal()}
        {this.renderCurrentSentenceImageModal()}
        {this.renderDiscussionModal()}
        {this.renderExplanationModal()}
        {this.renderFavoriteProPromoModal()}
        {this.renderGameSettingsModal()}
        {this.renderHelpModal()}
        {this.renderIgnoreModal()}
        {this.renderKnowItModal()}
        {this.renderLeveledUpModal()}
        {this.renderManageCollectionModal()}
        {this.renderMasterModal()}
        {this.renderMaxPlayedTodayModal()}
        {this.renderResetModal()}
        {this.renderReportErrorModal()}
        {this.renderResourceLinksEditorModal()}
        {this.renderRoundHistoryModal()}
        {this.renderSentenceEditorModal()}
      </>
    );
  }

  isRoundComplete(state = this.state) {
    const { currentSentenceIndex, collectionClozeSentences, loading } = state;
    return !loading && currentSentenceIndex >= collectionClozeSentences.length;
  }

  renderRoundHistoryBtn() {
    return (
      <button
        className="btn btn-default round-history"
        onClick={() => this.setState({ roundHistoryModalVisible: true })}
        title="Round history (alt+y)"
      >
        <Icon name="history" type="fa" /> History
      </button>
    );
  }

  renderReportErrorBtn() {
    if(!this.state.isSignedIn) {
      return null;
    }

    const collectionClozeSentence = this.getCurrentSentence();
    return (
      <ReportErrorBtn
        className="report-error btn btn-default"
        collectionClozeSentence={collectionClozeSentence}
        url={collectionClozeSentence.collectionClozeSentencesErrorUrl || this.state.reportErrorUrl}
      />
    );
  }

  renderReportErrorModal() {
    if(!this.state.reportErrorModalVisible || !this.state.isSignedIn) {
      return null;
    }

    const collectionClozeSentence = this.getCurrentSentence();

    return (
      <ReportErrorModal
        collectionClozeSentence={collectionClozeSentence}
        onHidden={() => this.setState({ reportErrorModalVisible: false })}
        url={collectionClozeSentence.collectionClozeSentencesErrorUrl || this.state.reportErrorUrl}
      />
    );
  }

  renderHelpBtn() {
    return (
      <button className="btn btn-default" data-target="#help-modal" data-toggle="modal">
        <i className="fa fa-info-circle" aria-hidden="true"></i> Help!
      </button>
    );
  }

  isNoSentences() {
    const { currentSentenceIndex, collectionClozeSentences, loading } = this.state;
    return !loading && currentSentenceIndex === 0 && !collectionClozeSentences.length;
  }

  renderNoSentences() {
    return (
      <div className="text-center">
        <h2>No more sentences to play!</h2>
        <p><img src="https://cdn.clozemaster.com/images/sad-pikachu.gif" /></p>
        <p>Check back later as sentences become ready for review.</p>
        <a href="/dashboard" className="btn btn-default joystix btn-lg">
          <Icon name="chevron-left" /> Dashboard
        </a>
      </div>
    );
  }

  renderCurrentSentenceSourceLink() {
    const currentSentence = this.getCurrentSentence();
    const { answered } = this.state;

    if(answered && currentSentence.tatoebaId) {
      return (
        <div style={{ marginTop: 10 }}>
          <a className="btn btn-xs btn-default" href={`https://tatoeba.org/eng/sentences/show/${currentSentence.tatoebaId}`} target="_blank" rel="noopener">
            Sentence Source <Icon name="new-window" />
          </a>
        </div>
      );
    }
  }

  renderExplanationRequest() {
    const currentSentence = this.getCurrentSentence();
    const { collectionId } = this.props;
    const { answered, languagePairingId } = this.state;

    if(!answered) {
      return null;
    }

    return (
      <ExplanationRequest
        collectionId={collectionId}
        collectionClozeSentenceId={currentSentence.id}
        languagePairingId={languagePairingId}
      />
    );
  }

  renderCopyCurrentSentenceToCollectionPopover() {
    if(!this.state.copyPopoverVisible) {
      return null;
    }

    const currentSentence = this.getCurrentSentence();
    const { collectionsUrl } = this.state;

    return (
      <CopySentencePopover
        collectionClozeSentence={currentSentence}
        collectionsUrl={collectionsUrl}
        onClose={() => this.setState({ copyPopoverVisible: false })}
        onCopy={() => null}
      />
    );
  }

  renderMaxPlayedTodayModal() {
    const { answered, maxPlayableToday, numPlayedToday, dailyReminderEmail, dailyReminderEmailUrl } = this.state;
    const { languagePairingWebUrl, dailyLimitDailyReminderEnabled, timeZone } = this.props;

    // A redo replays sentences already counted today, so the cap doesn't apply to it.
    if(!answered && maxPlayableToday && (numPlayedToday >= maxPlayableToday) && !this.isRedoRound()) {
      const shouldUseFreeLeftTodayPromo = this.props.shouldUseFreeLeftTodayPromo;

      if (shouldUseFreeLeftTodayPromo) {
        return (
          <V5MaxPlayedTodayModal
            amplitudeRoundProperties={this.amplitudeRoundProperties()}
            dailyReminder={{
              selected: dailyReminderEmail,
              show: dailyLimitDailyReminderEnabled,
              timeZone: timeZone,
              updateUrl: dailyReminderEmailUrl,
            }}
            languagePairingWebUrl={languagePairingWebUrl}
            sentenceLimit={maxPlayableToday}
          />
        );
      }

      return (
        <MaxPlayedTodayModal
          amplitudeRoundProperties={this.amplitudeRoundProperties()}
          dailyReminder={{
            selected: dailyReminderEmail,
            show: dailyLimitDailyReminderEnabled,
            timeZone: timeZone,
            updateUrl: dailyReminderEmailUrl,
          }}
          languagePairingWebUrl={languagePairingWebUrl}
          sentenceLimit={maxPlayableToday}
        />
      );
    }
  }

  getSentenceImageBackgroundStyles() {
    return {};
    // const currentSentence = this.getCurrentSentence();
    // if(!currentSentence || !currentSentence.imageUrl) {
    //   return null;
    // }

    // return {
    //   background: `url(${currentSentence.imageUrl})`
    // };
  }

  getClozeableElementStyle() {
    const style = { display: this.isPlayingFlashcard() ? "flex" : "block" };

    const { gameSettings } = this.state;

    if(gameSettings.imageBackground === "on") {
      const currentSentence = this.getCurrentSentence();
      if(currentSentence && currentSentence.imageUrl) {
        style.background = gameSettings.theme === "dark" ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.85)";
        style.borderRadius = 10;
        style.padding = 10;
      }
    }

    return style;
  }

  renderStage() {
    if(this.isNoSentences()) {
      return this.renderNoSentences();
    }

    if(this.isRoundComplete()) {
      return this.renderRoundResults();
    }

    const currentSentence = this.getCurrentSentence();

    return (
      <div className="stage">
        <div className={"clozeable" + (this.isPlayingFlashcard() ? " flashcard-clozeable" : "")} style={this.getClozeableElementStyle()}>
          {this.renderHeader()}
          {this.renderListeningControl()}
          {this.isPlayingFlashcard() ? this.renderFlashcard() : this.renderCurrentSentence()}
          {this.renderStrictDiacriticsHint()}
          {this.renderGameControls()}
          {this.renderSpeakingControl()}
          <div style={{ marginTop: 30, display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 6 }}>
            {/* a graded flashcard moves straight on, so these show once it's revealed and keep their space while hidden */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, visibility: this.isAnswerShown() ? "visible" : "hidden" }}>
              {/* Not on a landing page: a visitor there is one sentence into trying it out. */}
              {(this.state.answered || this.isPlayingFlashcard()) && !this.props.landing && (
                <>
                  <button className="btn btn-default control" id="discussion-modal-btn" title="Discuss sentence (alt+u)" onClick={() => this.setState({ discussionModalVisible: true })}>
                    <span className="glyphicon glyphicon-comment"></span>
                    {!!currentSentence.commentsCount && <span className="badge comment-count" style={{ marginLeft: 4 }}><span className="value">{currentSentence.commentsCount}</span></span>}
                    {" "}Discuss
                  </button>
                  <button className="btn btn-default control edit" title="Edit sentence (alt+d)" onClick={() => this.showSentenceEditorModal()}>
                    <span className="glyphicon glyphicon-pencil"></span> Edit
                  </button>
                  <button className="btn btn-default control add-to-collection" title="Add to collection (alt+a)" onClick={() => this.addCurrentSentenceToCollection()}>
                    <span className="glyphicon glyphicon-plus"></span> Save
                  </button>
                </>
              )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "flex-start" }}>
              {this.renderEndInfiniteRoundBtn()}
              <button className="btn btn-default game-settings" onClick={() => $("#game-settings-modal").modal("show")}><Icon name="cog" /></button>
              <DropDownMenu
                right
                button={
                  <button className="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-expanded="false">
                    <span className="glyphicon glyphicon-option-horizontal"></span>
                  </button>
                }
                lis={
                  <>
                    <li><a role="button" onClick={() => $("#help-modal").modal("show")}><i className="fa fa-info-circle" aria-hidden="true"></i> Help!</a></li>
                    <li><a role="button" onClick={() => this.setState({ roundHistoryModalVisible: true })}><Icon name="history" type="fa" /> History</a></li>
                    {this.state.isSignedIn && (
                      <li><a role="button" onClick={() => this.setState({ reportErrorModalVisible: true })}><span className="glyphicon glyphicon-flag"></span> Report</a></li>
                    )}
                    {this.isAnswerShown() && currentSentence.tatoebaId && (
                      <li><a href={`https://tatoeba.org/eng/sentences/show/${currentSentence.tatoebaId}`} target="_blank" rel="noopener"><Icon name="new-window" /> Sentence Source</a></li>
                    )}
                  </>
                }
              />
            </div>
          </div>
          {this.props.explanationRequestsEnabled && this.renderExplanationRequest()}
          {this.renderModals()}

          {/*
          {this.renderReplay()}
          {this.renderReportControl()}
          */}

          {this.props.test && this.isPlayingTextInput() && <span id="text-input-answer">{this.getCurrentSentenceCloze()}</span>}
          {this.props.test && this.isPlayingFullTextInput() && <span id="text-input-answer">{this.getCurrentSentencePlainText()}</span>}
        </div>
      </div>
    );
  }

  onStageFadeInComplete() {
    this.textInput && setTimeout(() => this.textInput.focus(), 0);
  }

  renderOnboardingNextOption({ btnClassName, btnText, explainer = null, onClick, text }) {
    return (
      <Panel style={{ margin: "0 auto 20px", maxWidth: 360 }}>
        <h4 style={{ marginTop: 0 }}>{text}</h4>
        {explainer && <p>{explainer}</p>}
        <button className={`btn btn-block btn-${btnClassName} joystix`} onClick={onClick}>{btnText}</button>
      </Panel>
    );
  }

  renderOnboardingNextOptions() {
    const { mode, skill } = this.props;
    const { collectionName, collectionTtsAvailable, isPro } = this.state;
    const isListeningSkillAvailable = !!collectionTtsAvailable;
    const isSpeakingSkillAvailable = !!(window.clozemaster.speechRecognition.isSupported(this.props.targetLanguageIso)
      && window.clozemaster.speechRecognition.isAvailable());

    return (
      <div className="container" style={{ marginTop: 40 }}>
        {this.renderOnboardingNextOption({ btnClassName: "success", btnText: <>Continue <Icon name="chevron-right" /></>, explainer: `${collectionName}, ${skill} skill option, ${mode.split("_").join(" ")}.`, onClick: () => this.nextRound(), text: <strong>Keep playing what I've been playing</strong> })}
        <div style={{ margin: "20px 0", textAlign: "center" }}>or</div>
        {(this.isPlayingTextInput() || this.isPlayingFullTextInput()) && this.renderOnboardingNextOption({ btnClassName: "success", btnText: <>Play <Icon name="chevron-right" /></>, explainer: "Select from four options for each sentence.", onClick: () => window.location.href = window.location.pathname + "?onboarding=true", text: "Play multiple choice" })}
        {!(this.isPlayingTextInput() || this.isPlayingFullTextInput()) && this.renderOnboardingNextOption({ btnClassName: "success", btnText: <>Play <Icon name="chevron-right" /></>, explainer: "Type in the missing word.", onClick: () => window.location.href = window.location.pathname + "?mode=text_input&onboarding=true", text: "Try text input" })}
        {isListeningSkillAvailable && !(this.isPlayingListening() && this.isPlayingMultipleChoice()) && this.renderOnboardingNextOption({ btnClassName: "success", btnText: <>Play <Icon name="chevron-right" /></>, explainer: "Hear the sentence before you see it then select the correct answer.", onClick: () => window.location.href = window.location.pathname + "?mode=multiple_choice&onboarding=true&skill=listening", text: "Try listening with multiple choice" })}
        {isListeningSkillAvailable && !(this.isPlayingListening() && this.isPlayingTextInput()) && this.renderOnboardingNextOption({ btnClassName: "success", btnText: <>Play <Icon name="chevron-right" /></>, explainer: "Hear the sentence before you see it then type in the correct answer.", onClick: () => window.location.href = window.location.pathname + "?mode=multiple_choice&onboarding=true&skill=listening", text: "Try listening with text input" })}
        {isListeningSkillAvailable && !(this.isPlayingListening() && this.isPlayingFullTextInput()) && this.renderOnboardingNextOption({ btnClassName: "success", btnText: <>Play <Icon name="chevron-right" /></>, explainer: "Type in the entire sentence based on what you hear.", onClick: () => window.location.href = window.location.pathname + "?mode=multiple_choice&onboarding=true&skill=listening", text: "Try listening with transcribe" })}
        {isSpeakingSkillAvailable && !(this.isPlayingSpeaking()) && this.renderOnboardingNextOption({ btnClassName: "success", btnText: <>Play <Icon name="chevron-right" /></>, explainer: "Use your microphone to speak the correct answer.", onClick: () => window.location.href = window.location.pathname + "?mode=multiple_choice&onboarding=true&skill=listening", text: "Try speaking" })}
        {this.renderOnboardingNextOption({ btnClassName: "default", btnText: <><Icon name="chevron-left" /> Dashboard</>, explainer: "View all collections, stats, and everything available on Clozemaster.", onClick: () => window.location.href = isPro ? "/" : "/welcome-pro", text: "Go to the Dashboard" })} 
      </div>
    );
  }

  render() {
    const { loading, intraRoundAd, intraRoundAdVisible, onboardingNextOptionsVisible } = this.state;

    if(loading) {
      return <Loading />;
    }

    if(onboardingNextOptionsVisible) {
      return this.renderOnboardingNextOptions();
    }

    if(intraRoundAdVisible) {
      return (
        <IntraRoundAd
          ad={intraRoundAd}
          nextRound={() => this.nextRound()}
          onLinkClick={() => this.resetRoundsPlayedCount()}
          onNextRoundable={() => this.setState({ intraRoundAdNextRoundable: true })}
        />
      );
    }

    return (
      <div>
        {!this.isRoundComplete() && <SlideDown>{this.renderRoundStats()}</SlideDown>}
        <div className="container">
          <FadeIn complete={() => this.onStageFadeInComplete()}>{this.renderStage()}</FadeIn>
        </div>
        {/*
        <SpeechToText
          languageLocale="es-ES"
          speechTokenUrl="/api/v1/speech_tokens"
        />
        */}
        {this.renderSoundEffects()}
      </div>
    );
  }
}
